import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Modal, Image, Platform, TouchableWithoutFeedback } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Picker } from '@react-native-picker/picker';
import { MessageSquare, Calendar, Home as HomeIcon, AlertTriangle, Clock, CheckCircle2, X, Trash2, Wrench, User, UserCheck, Edit3, ExternalLink, DollarSign, Paperclip, Download, Eye, File, Image as ImageIcon, Video, FileText, Search, LayoutList, List, ChevronDown, ChevronRight } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { downloadFile } from '../../lib/fileDownload';
import { ServiceProvider, ServiceRequestQuote, ServiceRequestExpense } from './types';
import { shadows } from '../../lib/styles';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VisitDurationDefaults,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
} from '../../lib/visitDuration';
import { loadVisitDurationDefaults } from '../../lib/visitDurationDefaults';

interface ServiceRequestNote {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  creator?: {
    full_name: string;
  } | null;
}

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  visit_id: string | null;
  visit_scheduled_at: string | null;
  visit_completed_at: string | null;
  owner_id: string | null;
  owner_assigned_at: string | null;
  owner_assigned_by: string | null;
  is_backburner: boolean;
  home: {
    id: string;
    address: string;
    city: string;
    state: string;
    owner: {
      id: string;
      full_name: string;
    } | null;
  } | null;
  assigned_owner?: {
    id: string;
    full_name: string;
  } | null;
  notes?: ServiceRequestNote[];
  quotes?: ServiceRequestQuote[];
  quotes_count?: number;
  accepted_quotes_count?: number;
  expenses_total?: number;
  expenses_paid?: number;
  expenses_unpaid?: number;
  expenses_homeowner_outstanding?: number;
  expenses_contractor_outstanding?: number;
}

type QueueFilter = 'all' | 'active' | 'backburner' | 'closed';

interface HomeownerCapacity {
  profile_id: string;
  active_count: number;
  backburner_count: number;
  effective_limit: number;
  over_limit_count: number;
}

const ServiceRequestsTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Read filters from URL params
  const statusFromUrl = searchParams.get('status');
  const ownerFromUrl = searchParams.get('owner');
  const queueFromUrl = searchParams.get('queue');
  
  // Parse status filter - support comma-separated values
  const parseStatusFromUrl = (statusParam: string | null): ServiceRequest['status'][] => {
    if (!statusParam) return [];
    const statuses = statusParam.split(',') as ServiceRequest['status'][];
    return statuses.filter(s => ['pending', 'in_progress', 'completed', 'cancelled'].includes(s));
  };
  
  const [statusFilter, setStatusFilter] = useState<ServiceRequest['status'][]>(
    parseStatusFromUrl(statusFromUrl)
  );
  
  // Parse owner filter - support comma-separated values
  const parseOwnerFromUrl = (ownerParam: string | null): string[] => {
    if (!ownerParam) return [];
    return ownerParam.split(',').filter(Boolean);
  };
  
  const [ownerFilter, setOwnerFilter] = useState<string[]>(
    parseOwnerFromUrl(ownerFromUrl)
  );
  
  // Parse priority filter - support comma-separated values
  const priorityFromUrl = searchParams.get('priority');
  const parsePriorityFromUrl = (priorityParam: string | null): ServiceRequest['priority'][] => {
    if (!priorityParam) return [];
    const priorities = priorityParam.split(',') as ServiceRequest['priority'][];
    return priorities.filter(p => ['low', 'normal', 'high', 'urgent'].includes(p));
  };
  
  const [priorityFilter, setPriorityFilter] = useState<ServiceRequest['priority'][]>(
    parsePriorityFromUrl(priorityFromUrl)
  );
  
  // Parse expense filter - support comma-separated values
  const expenseFromUrl = searchParams.get('expense');
  const parseExpenseFromUrl = (expenseParam: string | null): string[] => {
    if (!expenseParam) return [];
    return expenseParam.split(',').filter(Boolean);
  };
  
  const [expenseFilter, setExpenseFilter] = useState<string[]>(
    parseExpenseFromUrl(expenseFromUrl)
  );
  const parseQueueFromUrl = (queueParam: string | null): QueueFilter => {
    if (!queueParam) return 'all';
    if (queueParam === 'active' || queueParam === 'backburner' || queueParam === 'closed') {
      return queueParam;
    }
    return 'all';
  };
  const [queueFilter, setQueueFilter] = useState<QueueFilter>(
    parseQueueFromUrl(queueFromUrl)
  );
  
  // Read search query from URL params
  const searchFromUrl = searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(searchFromUrl);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [systemManagers, setSystemManagers] = useState<Array<{ id: string; full_name: string }>>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequestForDetail, setSelectedRequestForDetail] = useState<ServiceRequest | null>(null);
  const [notes, setNotes] = useState<ServiceRequestNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<ServiceRequestQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quoteAttachments, setQuoteAttachments] = useState<Record<string, any[]>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ServiceRequestExpense[]>([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
  const [selectedRequestForVisit, setSelectedRequestForVisit] = useState<ServiceRequest | null>(null);
  const [schedulingVisit, setSchedulingVisit] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(false);
  const [showStatusFilterDropdown, setShowStatusFilterDropdown] = useState(false);
  const [showOwnerFilterDropdown, setShowOwnerFilterDropdown] = useState(false);
  const [showPriorityFilterDropdown, setShowPriorityFilterDropdown] = useState(false);
  const [showExpenseFilterDropdown, setShowExpenseFilterDropdown] = useState(false);
  const [visitFormData, setVisitFormData] = useState({
    scheduled_date: '',
    estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE.service,
    assigned_to: '',
    service_provider_id: '',
    visit_notes: '',
    visit_type: 'service' as 'maintenance' | 'service' | 'walkthrough'
  });
  const [visitDurationDefaults, setVisitDurationDefaults] = useState<VisitDurationDefaults>({
    ...DEFAULT_VISIT_DURATION_BY_TYPE,
  });
  const [summaryData, setSummaryData] = useState({
    totalQuoted: 0,
    totalExpenses: 0,
    totalPaidExpenses: 0,
    totalUnpaidExpenses: 0,
    statusCounts: {
      pending: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0
    }
  });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [capacityByProfile, setCapacityByProfile] = useState<Record<string, HomeownerCapacity>>({});
  const [viewMode, setViewMode] = useState<'default' | 'compact'>('default');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    
    // Keep the tab parameter
    const currentTab = searchParams.get('tab');
    if (currentTab) {
      newParams.set('tab', currentTab);
    }
    
    // Update filter parameters - comma-separated for multiple values
    if (statusFilter.length > 0) {
      newParams.set('status', statusFilter.join(','));
    } else {
      newParams.delete('status');
    }
    
    if (ownerFilter.length > 0) {
      newParams.set('owner', ownerFilter.join(','));
    } else {
      newParams.delete('owner');
    }
    
    if (priorityFilter.length > 0) {
      newParams.set('priority', priorityFilter.join(','));
    } else {
      newParams.delete('priority');
    }
    
    if (expenseFilter.length > 0) {
      newParams.set('expense', expenseFilter.join(','));
    } else {
      newParams.delete('expense');
    }
    
    // Update search query
    if (searchQuery.trim()) {
      newParams.set('search', searchQuery.trim());
    } else {
      newParams.delete('search');
    }

    if (queueFilter !== 'all') {
      newParams.set('queue', queueFilter);
    } else {
      newParams.delete('queue');
    }

    setSearchParams(newParams, { replace: true });
  }, [statusFilter, ownerFilter, priorityFilter, expenseFilter, searchQuery, queueFilter]);

  // Sync URL params back to state when URL changes externally (e.g., browser back/forward)
  // This handles browser back/forward navigation
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    const ownerFromUrl = searchParams.get('owner');
    const priorityFromUrl = searchParams.get('priority');
    const expenseFromUrl = searchParams.get('expense');
    const searchFromUrl = searchParams.get('search') || '';
    const queueFromUrl = searchParams.get('queue');
    
    // Use functional updates to avoid dependency issues
    setStatusFilter(prev => {
      const newFilter = parseStatusFromUrl(statusFromUrl);
      return JSON.stringify(prev) !== JSON.stringify(newFilter) ? newFilter : prev;
    });
    setOwnerFilter(prev => {
      const newFilter = parseOwnerFromUrl(ownerFromUrl);
      return JSON.stringify(prev) !== JSON.stringify(newFilter) ? newFilter : prev;
    });
    setPriorityFilter(prev => {
      const newFilter = parsePriorityFromUrl(priorityFromUrl);
      return JSON.stringify(prev) !== JSON.stringify(newFilter) ? newFilter : prev;
    });
    setExpenseFilter(prev => {
      const newFilter = parseExpenseFromUrl(expenseFromUrl);
      return JSON.stringify(prev) !== JSON.stringify(newFilter) ? newFilter : prev;
    });
    setSearchQuery(prev => searchFromUrl !== prev ? searchFromUrl : prev);
    setQueueFilter(prev => {
      const newFilter = parseQueueFromUrl(queueFromUrl);
      return prev !== newFilter ? newFilter : prev;
    });
  }, [searchParams]);
  
  useEffect(() => {
    fetchRequests();
    fetchSystemManagers();
    fetchServiceProviders();
    fetchVisitDurationDefaults();
  }, []);

  const fetchVisitDurationDefaults = async () => {
    try {
      const defaults = await loadVisitDurationDefaults();
      setVisitDurationDefaults(defaults);
    } catch (error: any) {
      console.error('Error fetching visit duration defaults:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          priority,
          status,
          is_backburner,
          created_at,
          visit_id,
          visit_scheduled_at,
          visit_completed_at,
          owner_id,
          owner_assigned_at,
          owner_assigned_by,
          home:homes (
            id,
            address,
            city,
            state,
            owner:profiles!homes_owner_id_fkey (
              id,
              full_name
            )
          ),
          assigned_owner:profiles!owner_id (
            id,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch quotes count and expense summary for each request
      const requestsWithQuotesAndExpenses = await Promise.all(
        (data || []).map(async (request: any) => {
          // Fetch quotes
          const { data: quotesData, error: quotesError } = await supabase
            .from('service_request_quotes')
            .select('id, status')
            .eq('service_request_id', request.id);

          if (quotesError) {
            console.error('Error fetching quotes:', quotesError);
          }

          const quotesCount = quotesData?.length || 0;
          const acceptedQuotesCount = quotesData?.filter((q: any) => q.status === 'accepted').length || 0;

          // Fetch expenses
          const { data: expensesData, error: expensesError } = await supabase
            .from('service_request_expenses')
            .select('amount, is_paid, billable_to_homeowner, needs_contractor_payment, contractor_paid, parent_expense_id')
            .eq('service_request_id', request.id);

          if (expensesError) {
            console.error('Error fetching expenses:', expensesError);
          }

          const parentExpenses = (expensesData || []).filter(exp => !exp.parent_expense_id);

          const expensesTotal = parentExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
          const expensesPaid = parentExpenses.filter(exp => exp.is_paid || exp.contractor_paid).reduce((sum, exp) => sum + (exp.amount || 0), 0);
          const expensesHomeownerOutstanding = parentExpenses
            .filter(exp => exp.billable_to_homeowner && !exp.is_paid)
            .reduce((sum, exp) => sum + (exp.amount || 0), 0);
          const expensesContractorOutstanding = parentExpenses
            .filter(exp => exp.needs_contractor_payment && !exp.contractor_paid)
            .reduce((sum, exp) => sum + (exp.amount || 0), 0);
          const expensesUnpaid = expensesHomeownerOutstanding + expensesContractorOutstanding;

          return {
            ...request,
            quotes_count: quotesCount,
            accepted_quotes_count: acceptedQuotesCount,
            expenses_total: expensesTotal,
            expenses_paid: expensesPaid,
            expenses_unpaid: expensesUnpaid,
            expenses_homeowner_outstanding: expensesHomeownerOutstanding,
            expenses_contractor_outstanding: expensesContractorOutstanding,
          };
        })
      );

      const transformedRequests = (requestsWithQuotesAndExpenses as unknown as ServiceRequest[]) || [];
      setRequests(transformedRequests);
      await fetchHomeownerCapacity();
    } catch (error: any) {
      console.error('Error fetching service requests:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeownerCapacity = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_homeowner_request_capacity');
      if (error) throw error;

      const capacityMap = (data || []).reduce<Record<string, HomeownerCapacity>>((acc, item: any) => {
        acc[item.profile_id] = {
          profile_id: item.profile_id,
          active_count: item.active_count ?? 0,
          backburner_count: item.backburner_count ?? 0,
          effective_limit: item.effective_limit ?? 0,
          over_limit_count: item.over_limit_count ?? 0,
        };
        return acc;
      }, {});

      setCapacityByProfile(capacityMap);
    } catch (capacityError) {
      console.error('Error fetching homeowner request capacity:', capacityError);
      setCapacityByProfile({});
    }
  };

  const fetchSystemManagers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name');

      if (error) throw error;
      setSystemManagers(data || []);
    } catch (error) {
      console.error('Error fetching system managers:', error);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .order('company_name');

      if (error) throw error;
      setServiceProviders(data || []);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchNotes = async (requestId: string) => {
    try {
      // Fetch notes
      const { data: notesData, error: notesError } = await supabase
        .from('service_request_notes')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (notesError) throw notesError;

      if (!notesData || notesData.length === 0) {
        setNotes([]);
        return;
      }

      // Get unique user IDs from notes
      const userIds = [...new Set(notesData.map(note => note.created_by).filter(Boolean))];

      // Fetch user information for all creators
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (usersError) {
        console.error('Error fetching user info:', usersError);
        setNotes(notesData);
        return;
      }

      // Create a map of user IDs to user info
      const usersMap = new Map(usersData?.map(user => [user.id, user]) || []);

      // Enrich notes with user information
      const enrichedNotes = notesData.map(note => ({
        ...note,
        creator: usersMap.get(note.created_by) || null
      }));

      setNotes(enrichedNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleStatusUpdate = async (requestId: string, newStatus: ServiceRequest['status']) => {
    try {
      setUpdatingId(requestId);
      const { error } = await supabase
        .from('service_requests')
        .update({ status: newStatus })
        .eq('id', requestId);

      if (error) throw error;
      
      // Update local state
      setRequests(prev => prev.map(request => 
        request.id === requestId 
          ? { ...request, status: newStatus }
          : request
      ));
      
      setShowStatusModal(false);
      setSelectedRequest(null);
    } catch (error: any) {
      console.error('Error updating request status:', error);
      setError(error.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOwnerAssignment = async (requestId: string, ownerId: string | null) => {
    try {
      setUpdatingId(requestId);
      const { error } = await supabase
        .from('service_requests')
        .update({ owner_id: ownerId })
        .eq('id', requestId);

      if (error) throw error;
      
      // Refresh the requests list to get updated owner information
      fetchRequests();
    } catch (error: any) {
      console.error('Error updating request owner:', error);
      setError(error.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePriorityUpdate = async (requestId: string, newPriority: ServiceRequest['priority']) => {
    try {
      setUpdatingId(requestId);
      const { error } = await supabase
        .from('service_requests')
        .update({ priority: newPriority })
        .eq('id', requestId);

      if (error) throw error;
      
      // Update local state
      setRequests(prev => prev.map(request => 
        request.id === requestId 
          ? { ...request, priority: newPriority }
          : request
      ));
      
      setShowPriorityModal(false);
      setSelectedRequest(null);
    } catch (error: any) {
      console.error('Error updating request priority:', error);
      setError(error.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedRequestForDetail) return;

    setAddingNote(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('service_request_notes')
        .insert([{
          service_request_id: selectedRequestForDetail.id,
          content: newNote.trim(),
          created_by: user?.id || 'admin'
        }]);

      if (error) throw error;

      // Refetch notes to get the complete data with user information
      await fetchNotes(selectedRequestForDetail.id);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setDeletingNoteId(noteId);
    try {
      const { error } = await supabase
        .from('service_request_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      // Remove the note from local state
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    setDeletingRequest(true);
    try {
      // Delete the service request
      const { error } = await supabase
        .from('service_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      // Remove the request from local state
      setRequests(prev => prev.filter(request => request.id !== requestId));
      
      // Close modals
      setShowDeleteConfirmModal(false);
      setShowDetailModal(false);
      setSelectedRequestForDetail(null);
    } catch (error: any) {
      console.error('Error deleting service request:', error);
      setError(error.message);
    } finally {
      setDeletingRequest(false);
    }
  };

  const handleScheduleVisit = async () => {
    if (!selectedRequestForVisit) return;

    setSchedulingVisit(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create the visit
      const { data: visitData, error: visitError } = await supabase
        .from('visits')
        .insert([{
          home_id: selectedRequestForVisit.home?.id,
          visit_type: visitFormData.visit_type,
          scheduled_date: visitFormData.scheduled_date
            ? new Date(visitFormData.scheduled_date).toISOString()
            : null,
          estimated_duration_minutes: visitFormData.estimated_duration_minutes,
          assigned_to: visitFormData.assigned_to || null,
          service_provider_id: visitFormData.service_provider_id || null,
          visit_notes: visitFormData.visit_notes || null,
          created_by: user.id,
          updated_by: user.id
        }])
        .select()
        .single();

      if (visitError) throw visitError;

      // Create a visit task for this service request. Setting `task_id` is
      // what links the visit_task back to the service request, which the DB
      // trigger uses to keep `service_requests.visit_id` /
      // `visit_scheduled_at` in sync if the task is later removed.
      const { error: taskError } = await supabase
        .from('visit_tasks')
        .insert([{
          visit_id: visitData.id,
          task_type: 'service_request',
          task_id: selectedRequestForVisit.id,
          task_title: selectedRequestForVisit.title,
          task_description: selectedRequestForVisit.description,
          estimated_duration_minutes: visitFormData.estimated_duration_minutes
        }]);

      if (taskError) throw taskError;

      // Update the service request with visit information
      const { error: updateError } = await supabase
        .from('service_requests')
        .update({
          visit_id: visitData.id,
          visit_scheduled_at: visitData.scheduled_date,
          status: 'in_progress'
        })
        .eq('id', selectedRequestForVisit.id);

      if (updateError) throw updateError;

      // Reset form and close modal
      setVisitFormData({
        scheduled_date: '',
        estimated_duration_minutes: visitDurationDefaults.service,
        assigned_to: '',
        service_provider_id: '',
        visit_notes: '',
        visit_type: 'service'
      });
      setShowScheduleVisitModal(false);
      setSelectedRequestForVisit(null);

      // Refresh the requests list
      fetchRequests();
    } catch (error: any) {
      console.error('Error scheduling visit:', error);
      setError(error.message);
    } finally {
      setSchedulingVisit(false);
    }
  };

  const getPriorityColor = (priority: ServiceRequest['priority']) => {
    switch (priority) {
      case 'urgent': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      case 'high': return { color: '#ea580c', backgroundColor: '#ffedd5' };
      case 'normal': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'low': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      default: return { color: '#4b5563', backgroundColor: '#f3f4f6' };
    }
  };

  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'in_progress': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'cancelled': return { color: '#4b5563', backgroundColor: '#f3f4f6' };
      default: return { color: '#ea580c', backgroundColor: '#ffedd5' };
    }
  };

  const getQueueBucket = (request: ServiceRequest): Exclude<QueueFilter, 'all'> => {
    if (request.status === 'completed' || request.status === 'cancelled') {
      return 'closed';
    }

    // For now, all open low-priority requests are treated as backburner.
    if (request.priority === 'low' || request.is_backburner) {
      return 'backburner';
    }

    return 'active';
  };

  const toggleCardExpanded = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBackburnerToggle = async (request: ServiceRequest) => {
    try {
      setUpdatingId(request.id);
      const nextBackburnerValue = !request.is_backburner;
      const { error } = await supabase
        .from('service_requests')
        .update({ is_backburner: nextBackburnerValue })
        .eq('id', request.id);

      if (error) throw error;

      setRequests(prev =>
        prev.map(item =>
          item.id === request.id ? { ...item, is_backburner: nextBackburnerValue } : item
        )
      );

      await fetchHomeownerCapacity();
    } catch (toggleError) {
      console.error('Error updating backburner state:', toggleError);
      setError(toggleError instanceof Error ? toggleError.message : 'Failed to update request queue state');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredRequestsBeforeQueue = requests.filter(request => {
    // Filter by status - match if no statuses selected OR request status is in the selected statuses
    const statusMatch = statusFilter.length === 0 || statusFilter.includes(request.status);
    
    // Filter by priority - match if no priorities selected OR request priority is in the selected priorities
    const priorityMatch = priorityFilter.length === 0 || priorityFilter.includes(request.priority);
    
    // Filter by owner - match if no owners selected OR request owner is in the selected owners
    let ownerMatch = true;
    if (ownerFilter.length > 0) {
      if (ownerFilter.includes('unassigned')) {
        // If 'unassigned' is selected, match unassigned requests OR requests with selected owners
        const otherOwners = ownerFilter.filter(o => o !== 'unassigned');
        ownerMatch = !request.assigned_owner || 
                     (otherOwners.length > 0 && request.assigned_owner && otherOwners.includes(request.assigned_owner.id));
      } else {
        // Only match requests with selected owners
        ownerMatch = request.assigned_owner ? ownerFilter.includes(request.assigned_owner.id) : false;
      }
    }
    
    // Filter by search query (homeowner name or home address)
    let searchMatch = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const homeownerName = request.home?.owner?.full_name?.toLowerCase() || '';
      const homeAddress = `${request.home?.address || ''} ${request.home?.city || ''} ${request.home?.state || ''}`.toLowerCase();
      searchMatch = homeownerName.includes(query) || homeAddress.includes(query);
    }
    
    // Filter by expense type
    let expenseMatch = true;
    if (expenseFilter.length > 0) {
      expenseMatch = expenseFilter.some(filterType => {
        switch (filterType) {
          case 'no_expenses':
            return (request.expenses_total === 0 || request.expenses_total === undefined || request.expenses_total === null);
          case 'homeowner_outstanding':
            return (request.expenses_homeowner_outstanding || 0) > 0;
          case 'contractor_outstanding':
            return (request.expenses_contractor_outstanding || 0) > 0;
          case 'complete':
            return (request.expenses_total || 0) > 0 && (request.expenses_homeowner_outstanding || 0) === 0 && (request.expenses_contractor_outstanding || 0) === 0;
          default:
            return false;
        }
      });
    }
    
    return statusMatch && priorityMatch && ownerMatch && searchMatch && expenseMatch;
  });

  const queueCounts = filteredRequestsBeforeQueue.reduce(
    (acc, request) => {
      const bucket = getQueueBucket(request);
      acc[bucket] += 1;
      return acc;
    },
    { active: 0, backburner: 0, closed: 0 }
  );

  const filteredRequests = filteredRequestsBeforeQueue.filter((request) => {
    if (queueFilter === 'all') return true;
    return getQueueBucket(request) === queueFilter;
  });

  const homeownersOverLimit = Object.values(capacityByProfile).filter(
    (capacity) => capacity.over_limit_count > 0
  ).length;

  // Calculate summary whenever the filters or requests change
  useEffect(() => {
    if (requests.length > 0 || filteredRequests.length === 0) {
      calculateSummary(filteredRequests);
    }
  }, [requests.length, statusFilter.join(','), ownerFilter.join(','), priorityFilter.join(','), expenseFilter.join(','), searchQuery, queueFilter]);

  const handleRequestClick = (request: ServiceRequest) => {
    setSelectedRequestForDetail(request);
    setShowDetailModal(true);
    fetchNotes(request.id);
    fetchQuotes(request.id);
    fetchExpenses(request.id);
  };

  const fetchQuotes = async (requestId: string) => {
    try {
      setLoadingQuotes(true);
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          )
        `)
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface and fetch attachments
      const attachmentsMap: Record<string, any[]> = {};
      const transformedQuotes = await Promise.all(
        (data || []).map(async (quote: any) => {
          // Fetch full attachment data for each quote
          const { data: attachmentsData, error: attachmentsError } = await supabase
            .from('service_request_quote_attachments')
            .select('*')
            .eq('quote_id', quote.id)
            .order('created_at', { ascending: false });

          const attachments = attachmentsError ? [] : (attachmentsData || []);
          attachmentsMap[quote.id] = attachments;

          return {
            ...quote,
            service_provider: Array.isArray(quote.service_provider) 
              ? quote.service_provider[0] 
              : quote.service_provider,
            attachment_count: attachments.length
          };
        })
      );

      setQuotes(transformedQuotes);
      setQuoteAttachments(attachmentsMap);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const fetchExpenses = async (requestId: string) => {
    try {
      setLoadingExpenses(true);
      const { data, error } = await supabase
        .from('service_request_expenses')
        .select(`
          *,
          quote:service_request_quotes (
            id,
            amount,
            service_provider:service_providers (
              company_name
            )
          )
        `)
        .eq('service_request_id', requestId)
        .order('expense_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data
      const transformedExpenses = (data || []).map((expense: any) => ({
        ...expense,
        quote: Array.isArray(expense.quote) ? expense.quote[0] : expense.quote
      }));

      setExpenses(transformedExpenses);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const calculateSummary = async (requests: ServiceRequest[]) => {
    try {
      setLoadingSummary(true);
      
      // Calculate status counts
      const statusCounts = {
        pending: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0
      };
      
      requests.forEach(request => {
        statusCounts[request.status]++;
      });

      // If no requests, set empty summary
      if (requests.length === 0) {
        setSummaryData({
          totalQuoted: 0,
          totalExpenses: 0,
          totalPaidExpenses: 0,
          totalUnpaidExpenses: 0,
          statusCounts
        });
        return;
      }

      const requestIds = requests.map(r => r.id);

      // Fetch all quotes for these requests
      const { data: quotesData, error: quotesError } = await supabase
        .from('service_request_quotes')
        .select('amount')
        .in('service_request_id', requestIds);

      if (quotesError) throw quotesError;

      // Fetch all expenses for these requests
      const { data: expensesData, error: expensesError } = await supabase
        .from('service_request_expenses')
        .select('amount, is_paid, billable_to_homeowner, parent_expense_id')
        .in('service_request_id', requestIds);

      if (expensesError) throw expensesError;

      // Calculate totals
      const totalQuoted = (quotesData || []).reduce((sum, quote) => {
        return sum + (quote.amount || 0);
      }, 0);

      const parentExpenses = (expensesData || []).filter(expense => !expense.parent_expense_id);

      const totalExpenses = parentExpenses.reduce((sum, expense) => {
        return sum + (expense.amount || 0);
      }, 0);

      const totalPaidExpenses = parentExpenses
        .filter(expense => expense.is_paid)
        .reduce((sum, expense) => sum + (expense.amount || 0), 0);

      const totalUnpaidExpenses = parentExpenses
        .filter(expense => expense.billable_to_homeowner && !expense.is_paid)
        .reduce((sum, expense) => sum + (expense.amount || 0), 0);

      setSummaryData({
        totalQuoted,
        totalExpenses,
        totalPaidExpenses,
        totalUnpaidExpenses,
        statusCounts
      });
    } catch (error) {
      console.error('Error calculating summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const calculateTotalExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculatePaidExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && (expense.is_paid || expense.contractor_paid))
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculateHomeownerOutstandingExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && expense.billable_to_homeowner && !expense.is_paid)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculateContractorOutstandingExpenses = () => {
    return expenses
      .filter(expense => !expense.parent_expense_id && expense.needs_contractor_payment && !expense.contractor_paid)
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return 'N/A';
    const labels: Record<string, string> = {
      'credit_card': 'Credit Card',
      'check': 'Check',
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'other': 'Other',
      'stripe': 'Stripe',
    };
    return labels[method] || method;
  };

  const getQuoteStatusColor = (status: ServiceRequestQuote['status']) => {
    switch (status) {
      case 'accepted': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'rejected': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      default: return { color: '#ca8a04', backgroundColor: '#fef9c3' };
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const downloadAttachment = async (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const viewImage = (attachment: any) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={12} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <Video size={12} color="#6b7280" />;
    if (fileType === 'application/pdf') return <FileText size={12} color="#6b7280" />;
    return <File size={12} color="#6b7280" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  const toggleStatusFilter = (status: ServiceRequest['status']) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const getStatusFilterLabel = () => {
    if (statusFilter.length === 0) {
      return 'All Statuses';
    } else if (statusFilter.length === 1) {
      return statusFilter[0].split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    } else {
      return `${statusFilter.length} Statuses`;
    }
  };

  const toggleOwnerFilter = (ownerId: string) => {
    setOwnerFilter(prev => {
      if (prev.includes(ownerId)) {
        return prev.filter(o => o !== ownerId);
      } else {
        return [...prev, ownerId];
      }
    });
  };

  const getOwnerFilterLabel = () => {
    if (ownerFilter.length === 0) {
      return 'All Owners';
    } else if (ownerFilter.length === 1) {
      if (ownerFilter[0] === 'unassigned') {
        return 'Unassigned';
      }
      const manager = systemManagers.find(m => m.id === ownerFilter[0]);
      return manager?.full_name || 'Unknown';
    } else {
      return `${ownerFilter.length} Owners`;
    }
  };

  const togglePriorityFilter = (priority: ServiceRequest['priority']) => {
    setPriorityFilter(prev => {
      if (prev.includes(priority)) {
        return prev.filter(p => p !== priority);
      } else {
        return [...prev, priority];
      }
    });
  };

  const getPriorityFilterLabel = () => {
    if (priorityFilter.length === 0) {
      return 'All Priorities';
    } else if (priorityFilter.length === 1) {
      return priorityFilter[0].charAt(0).toUpperCase() + priorityFilter[0].slice(1);
    } else {
      return `${priorityFilter.length} Priorities`;
    }
  };
  
  const toggleExpenseFilter = (expenseType: string) => {
    setExpenseFilter(prev => {
      if (prev.includes(expenseType)) {
        return prev.filter(e => e !== expenseType);
      } else {
        return [...prev, expenseType];
      }
    });
  };
  
  const getExpenseFilterLabel = () => {
    if (expenseFilter.length === 0) {
      return 'All Expenses';
    } else if (expenseFilter.length === 1) {
      const labels: Record<string, string> = {
        'no_expenses': 'No Expenses',
        'homeowner_outstanding': 'Homeowner Outstanding',
        'contractor_outstanding': 'Contractor Outstanding',
        'complete': 'Complete Expenses',
      };
      return labels[expenseFilter[0]] || expenseFilter[0];
    } else {
      return `${expenseFilter.length} Expense Types`;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Error loading service requests</Text>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  return (
    <>
      <WebScrollView style={styles.tabScroll} contentContainerStyle={styles.tabScrollContent}>
        <View style={styles.header}>
          {/* First Row: Title and Search */}
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <MessageSquare size={24} color="#9ca3af" />
              <Text style={styles.title}>Service Requests</Text>
            </View>
            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Search size={16} color="#9ca3af" />
              <TextInput
                style={styles.searchInput}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search by homeowner or address..."
                placeholderTextColor="#9ca3af"
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery('')}>
                  <X size={16} color="#9ca3af" />
                </Pressable>
              )}
            </View>
          </View>

          {/* Second Row: Filters and Actions */}
          <View style={styles.headerRow}>
            <View style={styles.headerActions}>
            {/* Status Multi-Select Filter */}
            <View style={styles.filterContainer}>
              <Pressable
                style={styles.filterButton}
                onPress={() => setShowStatusFilterDropdown(!showStatusFilterDropdown)}
              >
                <Text style={styles.filterButtonText}>{getStatusFilterLabel()}</Text>
                <Text style={styles.filterButtonIcon}>▼</Text>
              </Pressable>
              
              {showStatusFilterDropdown && (
                <Modal
                  visible={showStatusFilterDropdown}
                  transparent
                  animationType="fade"
                  onRequestClose={() => setShowStatusFilterDropdown(false)}
                >
                  <TouchableWithoutFeedback onPress={() => setShowStatusFilterDropdown(false)}>
                    <View style={styles.modalOverlay}>
                      <View style={styles.statusFilterDropdown}>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            setStatusFilter([]);
                            setShowStatusFilterDropdown(false);
                          }}
                        >
                          <Text style={styles.statusFilterText}>All Statuses</Text>
                          {statusFilter.length === 0 && (
                            <CheckCircle2 size={16} color="#000000" />
                          )}
                        </Pressable>
                        <View style={styles.statusFilterDivider} />
                        {(['pending', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
                          <Pressable
                            key={status}
                            style={styles.statusFilterItem}
                            onPress={() => {
                              toggleStatusFilter(status);
                            }}
                          >
                            <View style={styles.statusFilterCheckbox}>
                              {statusFilter.includes(status) && (
                                <CheckCircle2 size={12} color="#ffffff" />
                              )}
                            </View>
                            <Text style={styles.statusFilterText}>
                              {status.split('_').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  </TouchableWithoutFeedback>
                </Modal>
              )}
            </View>
            
            {/* Owner Multi-Select Filter */}
            <View style={styles.filterContainer}>
              <Pressable
                style={styles.filterButton}
                onPress={() => setShowOwnerFilterDropdown(!showOwnerFilterDropdown)}
              >
                <Text style={styles.filterButtonText}>{getOwnerFilterLabel()}</Text>
                <Text style={styles.filterButtonIcon}>▼</Text>
              </Pressable>
              
              {showOwnerFilterDropdown && (
                <Modal
                  visible={showOwnerFilterDropdown}
                  transparent
                  animationType="fade"
                  onRequestClose={() => setShowOwnerFilterDropdown(false)}
                >
                  <TouchableWithoutFeedback onPress={() => setShowOwnerFilterDropdown(false)}>
                    <View style={styles.modalOverlay}>
                      <View style={styles.ownerFilterDropdown}>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            setOwnerFilter([]);
                            setShowOwnerFilterDropdown(false);
                          }}
                        >
                          <Text style={styles.statusFilterText}>All Owners</Text>
                          {ownerFilter.length === 0 && (
                            <CheckCircle2 size={16} color="#000000" />
                          )}
                        </Pressable>
                        <View style={styles.statusFilterDivider} />
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            toggleOwnerFilter('unassigned');
                          }}
                        >
                          <View style={styles.statusFilterCheckbox}>
                            {ownerFilter.includes('unassigned') && (
                              <CheckCircle2 size={12} color="#ffffff" />
                            )}
                          </View>
                          <Text style={styles.statusFilterText}>Unassigned</Text>
                        </Pressable>
                        <View style={styles.statusFilterDivider} />
                        {systemManagers.map((manager) => (
                          <Pressable
                            key={manager.id}
                            style={styles.statusFilterItem}
                            onPress={() => {
                              toggleOwnerFilter(manager.id);
                            }}
                          >
                            <View style={styles.statusFilterCheckbox}>
                              {ownerFilter.includes(manager.id) && (
                                <CheckCircle2 size={12} color="#ffffff" />
                              )}
                            </View>
                            <Text style={styles.statusFilterText}>{manager.full_name}</Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  </TouchableWithoutFeedback>
                </Modal>
              )}
            </View>
            
            {/* Priority Multi-Select Filter */}
            <View style={styles.filterContainer}>
              <Pressable
                style={styles.filterButton}
                onPress={() => setShowPriorityFilterDropdown(!showPriorityFilterDropdown)}
              >
                <Text style={styles.filterButtonText}>{getPriorityFilterLabel()}</Text>
                <Text style={styles.filterButtonIcon}>▼</Text>
              </Pressable>
              
              {showPriorityFilterDropdown && (
                <Modal
                  visible={showPriorityFilterDropdown}
                  transparent
                  animationType="fade"
                  onRequestClose={() => setShowPriorityFilterDropdown(false)}
                >
                  <TouchableWithoutFeedback onPress={() => setShowPriorityFilterDropdown(false)}>
                    <View style={styles.modalOverlay}>
                      <View style={styles.priorityFilterDropdown}>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            setPriorityFilter([]);
                            setShowPriorityFilterDropdown(false);
                          }}
                        >
                          <Text style={styles.statusFilterText}>All Priorities</Text>
                          {priorityFilter.length === 0 && (
                            <CheckCircle2 size={16} color="#000000" />
                          )}
                        </Pressable>
                        <View style={styles.statusFilterDivider} />
                        {(['urgent', 'high', 'normal', 'low'] as const).map((priority) => (
                          <Pressable
                            key={priority}
                            style={styles.statusFilterItem}
                            onPress={() => {
                              togglePriorityFilter(priority);
                            }}
                          >
                            <View style={styles.statusFilterCheckbox}>
                              {priorityFilter.includes(priority) && (
                                <CheckCircle2 size={12} color="#ffffff" />
                              )}
                            </View>
                            <Text style={styles.statusFilterText}>
                              {priority.charAt(0).toUpperCase() + priority.slice(1)}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  </TouchableWithoutFeedback>
                </Modal>
              )}
            </View>
            
            {/* Expense Multi-Select Filter */}
            <View style={styles.filterContainer}>
              <Pressable
                style={styles.filterButton}
                onPress={() => setShowExpenseFilterDropdown(!showExpenseFilterDropdown)}
              >
                <Text style={styles.filterButtonText}>{getExpenseFilterLabel()}</Text>
                <Text style={styles.filterButtonIcon}>▼</Text>
              </Pressable>
              
              {showExpenseFilterDropdown && (
                <Modal
                  visible={showExpenseFilterDropdown}
                  transparent
                  animationType="fade"
                  onRequestClose={() => setShowExpenseFilterDropdown(false)}
                >
                  <TouchableWithoutFeedback onPress={() => setShowExpenseFilterDropdown(false)}>
                    <View style={styles.modalOverlay}>
                      <View style={styles.priorityFilterDropdown}>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            setExpenseFilter([]);
                            setShowExpenseFilterDropdown(false);
                          }}
                        >
                          <Text style={styles.statusFilterText}>All Expenses</Text>
                          {expenseFilter.length === 0 && (
                            <CheckCircle2 size={16} color="#000000" />
                          )}
                        </Pressable>
                        <View style={styles.statusFilterDivider} />
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            toggleExpenseFilter('no_expenses');
                          }}
                        >
                          <View style={styles.statusFilterCheckbox}>
                            {expenseFilter.includes('no_expenses') && (
                              <CheckCircle2 size={12} color="#ffffff" />
                            )}
                          </View>
                          <Text style={styles.statusFilterText}>No Expenses</Text>
                        </Pressable>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            toggleExpenseFilter('homeowner_outstanding');
                          }}
                        >
                          <View style={styles.statusFilterCheckbox}>
                            {expenseFilter.includes('homeowner_outstanding') && (
                              <CheckCircle2 size={12} color="#ffffff" />
                            )}
                          </View>
                          <Text style={styles.statusFilterText}>Homeowner Outstanding</Text>
                        </Pressable>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            toggleExpenseFilter('contractor_outstanding');
                          }}
                        >
                          <View style={styles.statusFilterCheckbox}>
                            {expenseFilter.includes('contractor_outstanding') && (
                              <CheckCircle2 size={12} color="#ffffff" />
                            )}
                          </View>
                          <Text style={styles.statusFilterText}>Contractor Outstanding</Text>
                        </Pressable>
                        <Pressable
                          style={styles.statusFilterItem}
                          onPress={() => {
                            toggleExpenseFilter('complete');
                          }}
                        >
                          <View style={styles.statusFilterCheckbox}>
                            {expenseFilter.includes('complete') && (
                              <CheckCircle2 size={12} color="#ffffff" />
                            )}
                          </View>
                          <Text style={styles.statusFilterText}>Complete Expenses</Text>
                        </Pressable>
                      </View>
                    </View>
                  </TouchableWithoutFeedback>
                </Modal>
              )}
            </View>
            
            <Pressable
              style={styles.refreshButton}
              onPress={fetchRequests}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#4b5563" />
              ) : (
                <Text style={styles.refreshButtonText}>Refresh</Text>
              )}
            </Pressable>
            </View>
          </View>

          <View style={styles.queueFilterRow}>
            {([
              { key: 'all', label: 'All', count: filteredRequests.length },
              { key: 'active', label: 'Active', count: queueCounts.active },
              { key: 'backburner', label: 'Backburner', count: queueCounts.backburner },
              { key: 'closed', label: 'Completed/Cancelled', count: queueCounts.closed },
            ] as const).map((option) => (
              <Pressable
                key={option.key}
                style={[
                  styles.queueFilterChip,
                  queueFilter === option.key && styles.queueFilterChipActive,
                ]}
                onPress={() => setQueueFilter(option.key)}
              >
                <Text
                  style={[
                    styles.queueFilterChipText,
                    queueFilter === option.key && styles.queueFilterChipTextActive,
                  ]}
                >
                  {option.label}
                </Text>
                <Text
                  style={[
                    styles.queueFilterChipCount,
                    queueFilter === option.key && styles.queueFilterChipCountActive,
                  ]}
                >
                  {option.count}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* View Toggle */}
          <View style={styles.viewToggleRow}>
            <Pressable
              style={[styles.viewToggleButton, viewMode === 'default' && styles.viewToggleButtonActive]}
              onPress={() => setViewMode('default')}
            >
              <LayoutList size={14} color={viewMode === 'default' ? '#ffffff' : '#6b7280'} />
              <Text style={[styles.viewToggleButtonText, viewMode === 'default' && styles.viewToggleButtonTextActive]}>
                Full
              </Text>
            </Pressable>
            <Pressable
              style={[styles.viewToggleButton, viewMode === 'compact' && styles.viewToggleButtonActive]}
              onPress={() => setViewMode('compact')}
            >
              <List size={14} color={viewMode === 'compact' ? '#ffffff' : '#6b7280'} />
              <Text style={[styles.viewToggleButtonText, viewMode === 'compact' && styles.viewToggleButtonTextActive]}>
                Compact
              </Text>
            </Pressable>
          </View>
        </View>

        {/* Summary Widget */}
        <View style={styles.summaryWidget}>
          <Text style={styles.summaryWidgetTitle}>Summary</Text>
          
          {loadingSummary ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color="#9ca3af" />
            </View>
          ) : (
            <>
              {/* Status Counts */}
              <View style={styles.summarySection}>
                <Text style={styles.summarySectionTitle}>Status Breakdown</Text>
                <View style={styles.statusSummaryGrid}>
                  <View style={styles.statusSummaryItem}>
                    <View style={[styles.statusSummaryBadge, { backgroundColor: '#ffedd5' }]}>
                      <Text style={[styles.statusSummaryCount, { color: '#ea580c' }]}>
                        {summaryData.statusCounts.pending}
                      </Text>
                    </View>
                    <Text style={styles.statusSummaryLabel}>Pending</Text>
                  </View>
                  <View style={styles.statusSummaryItem}>
                    <View style={[styles.statusSummaryBadge, { backgroundColor: '#dbeafe' }]}>
                      <Text style={[styles.statusSummaryCount, { color: '#2563eb' }]}>
                        {summaryData.statusCounts.in_progress}
                      </Text>
                    </View>
                    <Text style={styles.statusSummaryLabel}>In Progress</Text>
                  </View>
                  <View style={styles.statusSummaryItem}>
                    <View style={[styles.statusSummaryBadge, { backgroundColor: '#dcfce7' }]}>
                      <Text style={[styles.statusSummaryCount, { color: '#16a34a' }]}>
                        {summaryData.statusCounts.completed}
                      </Text>
                    </View>
                    <Text style={styles.statusSummaryLabel}>Completed</Text>
                  </View>
                  <View style={styles.statusSummaryItem}>
                    <View style={[styles.statusSummaryBadge, { backgroundColor: '#f3f4f6' }]}>
                      <Text style={[styles.statusSummaryCount, { color: '#4b5563' }]}>
                        {summaryData.statusCounts.cancelled}
                      </Text>
                    </View>
                    <Text style={styles.statusSummaryLabel}>Cancelled</Text>
                  </View>
                </View>
              </View>

              <View style={styles.summarySection}>
                <Text style={styles.summarySectionTitle}>Capacity Overview</Text>
                <View style={styles.capacitySummaryRow}>
                  <View style={styles.capacitySummaryItem}>
                    <Text style={styles.capacitySummaryLabel}>Active Queue</Text>
                    <Text style={styles.capacitySummaryValue}>{queueCounts.active}</Text>
                  </View>
                  <View style={styles.capacitySummaryItem}>
                    <Text style={styles.capacitySummaryLabel}>Backburner Queue</Text>
                    <Text style={styles.capacitySummaryValue}>{queueCounts.backburner}</Text>
                  </View>
                  <View style={styles.capacitySummaryItem}>
                    <Text style={styles.capacitySummaryLabel}>Homeowners Over Limit</Text>
                    <Text style={[styles.capacitySummaryValue, homeownersOverLimit > 0 && styles.capacitySummaryValueAlert]}>
                      {homeownersOverLimit}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Financial Summary */}
              <View style={styles.summarySection}>
                <Text style={styles.summarySectionTitle}>Financial Summary</Text>
                <View style={styles.financialSummaryGrid}>
                  <View style={styles.financialSummaryItem}>
                    <View style={styles.financialSummaryHeader}>
                      <DollarSign size={16} color="#6b7280" />
                      <Text style={styles.financialSummaryLabel}>Total Quoted</Text>
                    </View>
                    <Text style={styles.financialSummaryAmount}>
                      {formatCurrency(summaryData.totalQuoted)}
                    </Text>
                  </View>
                  <View style={styles.financialSummaryItem}>
                    <View style={styles.financialSummaryHeader}>
                      <DollarSign size={16} color="#6b7280" />
                      <Text style={styles.financialSummaryLabel}>Total Expenses</Text>
                    </View>
                    <Text style={styles.financialSummaryAmount}>
                      {formatCurrency(summaryData.totalExpenses)}
                    </Text>
                  </View>
                  <View style={styles.financialSummaryItem}>
                    <View style={styles.financialSummaryHeader}>
                      <CheckCircle2 size={16} color="#16a34a" />
                      <Text style={styles.financialSummaryLabel}>Paid</Text>
                    </View>
                    <Text style={[styles.financialSummaryAmount, { color: '#16a34a' }]}>
                      {formatCurrency(summaryData.totalPaidExpenses)}
                    </Text>
                  </View>
                  <View style={styles.financialSummaryItem}>
                    <View style={styles.financialSummaryHeader}>
                      <Clock size={16} color="#dc2626" />
                      <Text style={styles.financialSummaryLabel}>Homeowner Outstanding</Text>
                    </View>
                    <Text style={[styles.financialSummaryAmount, { color: '#dc2626' }]}>
                      {formatCurrency(summaryData.totalUnpaidExpenses)}
                    </Text>
                  </View>
                </View>
              </View>
            </>
          )}
        </View>

        {filteredRequests.length === 0 ? (
          <View style={styles.emptyContainer}>
            <AlertTriangle size={48} color="#9ca3af" />
            <Text style={styles.emptyTitle}>No Service Requests Found</Text>
            <Text style={styles.emptyText}>
              {statusFilter.length === 0 &&
              ownerFilter.length === 0 &&
              priorityFilter.length === 0 &&
              expenseFilter.length === 0 &&
              queueFilter === 'all' &&
              !searchQuery.trim()
                ? 'There are no service requests in the system.'
                : `No service requests match the selected filters${searchQuery.trim() ? ' or search query' : ''}.`}
            </Text>
            {searchQuery.trim() && (
              <Pressable
                style={styles.clearSearchButton}
                onPress={() => setSearchQuery('')}
              >
                <Text style={styles.clearSearchButtonText}>Clear Search</Text>
              </Pressable>
            )}
          </View>
        ) : (
          <View style={[styles.requestsList, viewMode === 'compact' && styles.requestsListCompact]}>
            {filteredRequests.map((request) => {
              const priorityColors = getPriorityColor(request.priority);
              const statusColors = getStatusColor(request.status);
              const isAutoBackburner = request.priority === 'low' && (request.status === 'pending' || request.status === 'in_progress');
              const homeownerCapacity = request.home?.owner?.id
                ? capacityByProfile[request.home.owner.id]
                : undefined;
              const isExpanded = expandedCards.has(request.id);

              if (viewMode === 'compact') {
                return (
                  <View key={request.id} style={styles.compactCard}>
                    <Pressable
                      style={styles.compactCardHeader}
                      onPress={() => toggleCardExpanded(request.id)}
                    >
                      <View style={styles.compactCardLeft}>
                        {isExpanded ? (
                          <ChevronDown size={14} color="#9ca3af" />
                        ) : (
                          <ChevronRight size={14} color="#9ca3af" />
                        )}
                        <Pressable
                          style={[styles.compactStatusBadge, { backgroundColor: statusColors.backgroundColor }]}
                          onPress={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                            setShowStatusModal(true);
                          }}
                        >
                          <Text style={[styles.compactStatusText, { color: statusColors.color }]}>
                            {request.status.split('_').map(word =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Text>
                        </Pressable>
                        <View style={[styles.compactPriorityDot, { backgroundColor: priorityColors.color }]} />
                        <Text style={styles.compactTitle} numberOfLines={1}>{request.title}</Text>
                        <Text style={styles.compactOwnerName} numberOfLines={1}>
                          {request.home?.owner?.full_name || 'Unknown'}
                        </Text>
                      </View>
                      <View style={styles.compactCardRight}>
                        {request.assigned_owner && (
                          <View style={styles.compactAssignedBadge}>
                            <UserCheck size={11} color="#16a34a" />
                            <Text style={styles.compactAssignedText} numberOfLines={1}>
                              {request.assigned_owner.full_name}
                            </Text>
                          </View>
                        )}
                        {request.visit_id && (
                          <Calendar size={12} color="#16a34a" />
                        )}
                        {request.expenses_homeowner_outstanding !== undefined && request.expenses_homeowner_outstanding > 0 && (
                          <Text style={styles.compactExpenseFlag}>
                            {formatCurrency(request.expenses_homeowner_outstanding)} due
                          </Text>
                        )}
                        <Pressable
                          style={styles.compactDetailsButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            const nativeEvent = e.nativeEvent as unknown as MouseEvent;
                            if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
                              window.open(`/admin/service-request/${request.id}`, '_blank');
                            } else {
                              navigate(`/admin/service-request/${request.id}`);
                            }
                          }}
                        >
                          <ExternalLink size={11} color="#ffffff" />
                        </Pressable>
                      </View>
                    </Pressable>

                    {isExpanded && (
                      <View style={styles.compactCardBody}>
                        <Text style={styles.requestDescription}>{request.description}</Text>
                        <View style={styles.requestMeta}>
                          <View style={styles.metaItem}>
                            <HomeIcon size={14} color="#9ca3af" />
                            <Text style={styles.metaText}>
                              {request.home?.address}, {request.home?.city}, {request.home?.state}
                            </Text>
                          </View>
                          <View style={styles.metaItem}>
                            <Clock size={14} color="#9ca3af" />
                            <Text style={styles.metaText}>
                              {new Date(request.created_at).toLocaleDateString()}
                            </Text>
                          </View>
                          {homeownerCapacity && (
                            <View style={styles.capacityPill}>
                              <Text style={styles.capacityPillText}>
                                {homeownerCapacity.active_count}/{homeownerCapacity.effective_limit} active
                              </Text>
                              {homeownerCapacity.over_limit_count > 0 && (
                                <Text style={styles.capacityPillOverLimit}>
                                  +{homeownerCapacity.over_limit_count} over
                                </Text>
                              )}
                            </View>
                          )}
                          {request.quotes_count !== undefined && request.quotes_count > 0 && (
                            <View style={styles.metaItem}>
                              <DollarSign size={14} color="#9ca3af" />
                              <Text style={styles.metaText}>
                                {request.quotes_count} quote{request.quotes_count !== 1 ? 's' : ''}
                                {request.accepted_quotes_count !== undefined && request.accepted_quotes_count > 0 && (
                                  <Text style={styles.acceptedQuotesText}>
                                    {' '}({request.accepted_quotes_count} accepted)
                                  </Text>
                                )}
                              </Text>
                            </View>
                          )}
                        </View>

                        {request.expenses_total !== undefined && request.expenses_total > 0 && (
                          <View style={styles.expensesSummaryCard}>
                            <Text style={styles.expensesSummaryTitle}>Expenses</Text>
                            <View style={styles.expensesSummaryRow}>
                              <View style={styles.expensesSummaryItem}>
                                <Text style={styles.expensesSummaryLabel}>Total</Text>
                                <Text style={styles.expensesSummaryValue}>
                                  {formatCurrency(request.expenses_total)}
                                </Text>
                              </View>
                              {request.expenses_paid !== undefined && request.expenses_paid > 0 && (
                                <View style={styles.expensesSummaryItem}>
                                  <Text style={styles.expensesSummaryLabel}>Any Paid</Text>
                                  <Text style={[styles.expensesSummaryValue, { color: '#16a34a' }]}>
                                    {formatCurrency(request.expenses_paid)}
                                  </Text>
                                </View>
                              )}
                              {request.expenses_homeowner_outstanding !== undefined && request.expenses_homeowner_outstanding > 0 && (
                                <View style={styles.expensesSummaryItem}>
                                  <Text style={styles.expensesSummaryLabel}>Homeowner Outstanding</Text>
                                  <Text style={[styles.expensesSummaryValue, { color: '#dc2626' }]}>
                                    {formatCurrency(request.expenses_homeowner_outstanding)}
                                  </Text>
                                </View>
                              )}
                              {request.expenses_contractor_outstanding !== undefined && request.expenses_contractor_outstanding > 0 && (
                                <View style={styles.expensesSummaryItem}>
                                  <Text style={styles.expensesSummaryLabel}>Contractor Outstanding</Text>
                                  <Text style={[styles.expensesSummaryValue, { color: '#c2410c' }]}>
                                    {formatCurrency(request.expenses_contractor_outstanding)}
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>
                        )}

                        <View style={styles.compactCardActions}>
                          <View style={[styles.badge, { backgroundColor: priorityColors.backgroundColor }]}>
                            <Text style={[styles.badgeText, { color: priorityColors.color }]}>
                              {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                            </Text>
                          </View>
                          {request.visit_id ? (
                            <View style={styles.visitScheduledBadge}>
                              <Calendar size={12} color="#16a34a" />
                              <Text style={styles.visitScheduledText}>Visit Scheduled</Text>
                            </View>
                          ) : request.status !== 'completed' && request.status !== 'cancelled' && (
                            <Pressable
                              style={styles.scheduleButton}
                              onPress={() => {
                                setSelectedRequestForVisit(request);
                                setShowScheduleVisitModal(true);
                              }}
                            >
                              <Wrench size={12} color="#2563eb" />
                              <Text style={styles.scheduleButtonText}>Schedule Visit</Text>
                            </Pressable>
                          )}
                          {!request.assigned_owner && (
                            <Pressable
                              style={styles.assignButton}
                              onPress={() => {
                                setSelectedRequest(request);
                                setShowOwnerModal(true);
                              }}
                            >
                              <User size={12} color="#4b5563" />
                              <Text style={styles.assignButtonText}>Assign</Text>
                            </Pressable>
                          )}
                          {(request.status === 'pending' || request.status === 'in_progress') && !isAutoBackburner && (
                            <Pressable
                              style={[
                                styles.backburnerButton,
                                request.is_backburner && styles.backburnerButtonActive,
                              ]}
                              onPress={() => handleBackburnerToggle(request)}
                              disabled={updatingId === request.id}
                            >
                              <Text
                                style={[
                                  styles.backburnerButtonText,
                                  request.is_backburner && styles.backburnerButtonTextActive,
                                ]}
                              >
                                {request.is_backburner ? 'Move to Active' : 'Move to Backburner'}
                              </Text>
                            </Pressable>
                          )}
                          {isAutoBackburner && (
                            <View style={styles.lowPriorityBackburnerBadge}>
                              <Text style={styles.lowPriorityBackburnerBadgeText}>
                                Low priority -&gt; Backburner
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>
                    )}
                  </View>
                );
              }

              return (
                <View
                  key={request.id}
                  style={styles.requestCard}
                >
                  <View style={styles.requestContent}>
                    <View style={styles.requestInfo}>
                      <Text style={styles.requestTitle}>{request.title}</Text>
                      <Text style={styles.requestOwner}>
                        {request.home?.owner?.full_name || 'Unknown Owner'}
                      </Text>
                      <Text style={styles.requestDescription}>{request.description}</Text>
                      <View style={styles.requestMeta}>
                        <View style={styles.metaItem}>
                          <HomeIcon size={16} color="#9ca3af" />
                          <Text style={styles.metaText}>
                            {request.home?.address}, {request.home?.city}, {request.home?.state}
                          </Text>
                        </View>
                        <View style={styles.metaItem}>
                          <Clock size={16} color="#9ca3af" />
                          <Text style={styles.metaText}>
                            {new Date(request.created_at).toLocaleDateString()}
                          </Text>
                        </View>
                        {request.assigned_owner && (
                          <View style={styles.metaItem}>
                            <UserCheck size={16} color="#16a34a" />
                            <Text style={styles.metaTextAssigned}>
                              {request.assigned_owner.full_name}
                            </Text>
                          </View>
                        )}
                        {homeownerCapacity && (
                          <View style={styles.capacityPill}>
                            <Text style={styles.capacityPillText}>
                              {homeownerCapacity.active_count}/{homeownerCapacity.effective_limit} active
                            </Text>
                            {homeownerCapacity.over_limit_count > 0 && (
                              <Text style={styles.capacityPillOverLimit}>
                                +{homeownerCapacity.over_limit_count} over
                              </Text>
                            )}
                          </View>
                        )}
                        {request.quotes_count !== undefined && request.quotes_count > 0 && (
                          <View style={styles.metaItem}>
                            <DollarSign size={16} color="#9ca3af" />
                            <Text style={styles.metaText}>
                              {request.quotes_count} quote{request.quotes_count !== 1 ? 's' : ''}
                              {request.accepted_quotes_count !== undefined && request.accepted_quotes_count > 0 && (
                                <Text style={styles.acceptedQuotesText}>
                                  {' '}({request.accepted_quotes_count} accepted)
                                </Text>
                              )}
                            </Text>
                          </View>
                        )}
                      </View>
                      
                      {/* Expense Summary */}
                      {request.expenses_total !== undefined && request.expenses_total > 0 && (
                        <View style={styles.expensesSummaryCard}>
                          <Text style={styles.expensesSummaryTitle}>Expenses</Text>
                          <View style={styles.expensesSummaryRow}>
                            <View style={styles.expensesSummaryItem}>
                              <Text style={styles.expensesSummaryLabel}>Total</Text>
                              <Text style={styles.expensesSummaryValue}>
                                {formatCurrency(request.expenses_total)}
                              </Text>
                            </View>
                            {request.expenses_paid !== undefined && request.expenses_paid > 0 && (
                              <View style={styles.expensesSummaryItem}>
                                <Text style={styles.expensesSummaryLabel}>Any Paid</Text>
                                <Text style={[styles.expensesSummaryValue, { color: '#16a34a' }]}>
                                  {formatCurrency(request.expenses_paid)}
                                </Text>
                              </View>
                            )}
                            {request.expenses_homeowner_outstanding !== undefined && request.expenses_homeowner_outstanding > 0 && (
                              <View style={styles.expensesSummaryItem}>
                                <Text style={styles.expensesSummaryLabel}>Homeowner Outstanding</Text>
                                <Text style={[styles.expensesSummaryValue, { color: '#dc2626' }]}>
                                  {formatCurrency(request.expenses_homeowner_outstanding)}
                                </Text>
                              </View>
                            )}
                            {request.expenses_contractor_outstanding !== undefined && request.expenses_contractor_outstanding > 0 && (
                              <View style={styles.expensesSummaryItem}>
                                <Text style={styles.expensesSummaryLabel}>Contractor Outstanding</Text>
                                <Text style={[styles.expensesSummaryValue, { color: '#c2410c' }]}>
                                  {formatCurrency(request.expenses_contractor_outstanding)}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                    <View style={styles.requestActions}>
                      <View style={[styles.badge, { backgroundColor: priorityColors.backgroundColor }]}>
                        <Text style={[styles.badgeText, { color: priorityColors.color }]}>
                          {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                        </Text>
                      </View>
                      
                      {/* Visit Status or Schedule Button */}
                      {request.visit_id ? (
                        <View style={styles.visitScheduledBadge}>
                          <Calendar size={12} color="#16a34a" />
                          <Text style={styles.visitScheduledText}>Visit Scheduled</Text>
                        </View>
                      ) : request.status !== 'completed' && request.status !== 'cancelled' && (
                        <Pressable
                          style={styles.scheduleButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            setSelectedRequestForVisit(request);
                            setShowScheduleVisitModal(true);
                          }}
                        >
                          <Wrench size={12} color="#2563eb" />
                          <Text style={styles.scheduleButtonText}>Schedule Visit</Text>
                        </Pressable>
                      )}
                      
                      {/* Assign Owner Button */}
                      {!request.assigned_owner && (
                        <Pressable
                          style={styles.assignButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                            setShowOwnerModal(true);
                          }}
                        >
                          <User size={12} color="#4b5563" />
                          <Text style={styles.assignButtonText}>Assign</Text>
                        </Pressable>
                      )}

                      {(request.status === 'pending' || request.status === 'in_progress') && !isAutoBackburner && (
                        <Pressable
                          style={[
                            styles.backburnerButton,
                            request.is_backburner && styles.backburnerButtonActive,
                          ]}
                          onPress={(e) => {
                            e.stopPropagation();
                            handleBackburnerToggle(request);
                          }}
                          disabled={updatingId === request.id}
                        >
                          <Text
                            style={[
                              styles.backburnerButtonText,
                              request.is_backburner && styles.backburnerButtonTextActive,
                            ]}
                          >
                            {request.is_backburner ? 'Move to Active' : 'Move to Backburner'}
                          </Text>
                        </Pressable>
                      )}
                      {isAutoBackburner && (
                        <View style={styles.lowPriorityBackburnerBadge}>
                          <Text style={styles.lowPriorityBackburnerBadgeText}>
                            Low priority -&gt; Backburner
                          </Text>
                        </View>
                      )}
                      
                      <Pressable
                        style={[styles.statusButton, { backgroundColor: statusColors.backgroundColor }]}
                        onPress={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(request);
                          setShowStatusModal(true);
                        }}
                        disabled={updatingId === request.id}
                      >
                        {updatingId === request.id ? (
                          <ActivityIndicator size="small" color={statusColors.color} />
                        ) : (
                          <Text style={[styles.statusButtonText, { color: statusColors.color }]}>
                            {request.status.split('_').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Text>
                        )}
                      </Pressable>
                      
                      {/* View Details Button */}
                      <Pressable
                        style={styles.viewDetailsButtonSmall}
                        onPress={(e) => {
                          const nativeEvent = e.nativeEvent as unknown as MouseEvent;
                          if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
                            window.open(`/admin/service-request/${request.id}`, '_blank');
                          } else {
                            navigate(`/admin/service-request/${request.id}`);
                          }
                        }}
                      >
                        <ExternalLink size={12} color="#ffffff" />
                        <Text style={styles.viewDetailsButtonSmallText}>Details</Text>
                      </Pressable>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </WebScrollView>

      {/* Service Request Detail Modal */}
      <Modal
        visible={showDetailModal && !!selectedRequestForDetail}
        transparent
        animationType="slide"
        onRequestClose={() => setShowDetailModal(false)}
      >
        {selectedRequestForDetail && (
          <View style={styles.modalOverlay}>
            <View style={styles.detailModalContent}>
              <View style={styles.detailModalHeader}>
                <View style={styles.detailModalHeaderLeft}>
                  <Text style={styles.detailModalTitle}>{selectedRequestForDetail.title}</Text>
                  <Text style={styles.detailModalOwner}>
                    {selectedRequestForDetail.home?.owner?.full_name || 'Unknown Owner'}
                  </Text>
                  <View style={styles.detailModalMeta}>
                    <View style={styles.metaItem}>
                      <HomeIcon size={16} color="#6b7280" />
                      <Text style={styles.metaText}>
                        {selectedRequestForDetail.home?.address}, {selectedRequestForDetail.home?.city}, {selectedRequestForDetail.home?.state}
                      </Text>
                    </View>
                    <View style={styles.metaItem}>
                      <Clock size={16} color="#6b7280" />
                      <Text style={styles.metaText}>
                        Created: {new Date(selectedRequestForDetail.created_at).toLocaleDateString()}
                      </Text>
                    </View>
                  </View>
                </View>
                <Pressable
                  onPress={() => setShowDetailModal(false)}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.detailModalBody} contentContainerStyle={styles.detailModalBodyContent}>
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Description</Text>
                  <View style={styles.descriptionBox}>
                    <Text style={styles.descriptionText}>
                      {selectedRequestForDetail.description}
                    </Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <View style={styles.detailColumn}>
                    <Text style={styles.detailSectionTitle}>Priority</Text>
                    <View style={styles.detailValueRow}>
                      <View style={[styles.badge, { backgroundColor: getPriorityColor(selectedRequestForDetail.priority).backgroundColor }]}>
                        <Text style={[styles.badgeText, { color: getPriorityColor(selectedRequestForDetail.priority).color }]}>
                          {selectedRequestForDetail.priority.charAt(0).toUpperCase() + selectedRequestForDetail.priority.slice(1)}
                        </Text>
                      </View>
                      <Pressable
                        onPress={() => {
                          setShowDetailModal(false);
                          setSelectedRequest(selectedRequestForDetail);
                          setShowPriorityModal(true);
                        }}
                      >
                        <Edit3 size={16} color="#6b7280" />
                      </Pressable>
                    </View>
                  </View>
                  <View style={styles.detailColumn}>
                    <Text style={styles.detailSectionTitle}>Status</Text>
                    <View style={styles.detailValueRow}>
                      <View style={[styles.badge, { backgroundColor: getStatusColor(selectedRequestForDetail.status).backgroundColor }]}>
                        <Text style={[styles.badgeText, { color: getStatusColor(selectedRequestForDetail.status).color }]}>
                          {selectedRequestForDetail.status.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </Text>
                      </View>
                      <Pressable
                        onPress={() => {
                          setShowDetailModal(false);
                          setSelectedRequest(selectedRequestForDetail);
                          setShowStatusModal(true);
                        }}
                      >
                        <Edit3 size={16} color="#6b7280" />
                      </Pressable>
                    </View>
                  </View>
                </View>

                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Assigned Owner</Text>
                  <View style={styles.ownerRow}>
                    {selectedRequestForDetail.assigned_owner ? (
                      <View style={styles.ownerInfo}>
                        <UserCheck size={16} color="#16a34a" />
                        <Text style={styles.ownerName}>
                          {selectedRequestForDetail.assigned_owner.full_name}
                        </Text>
                      </View>
                    ) : (
                      <View style={styles.ownerInfo}>
                        <User size={16} color="#9ca3af" />
                        <Text style={styles.ownerUnassigned}>
                          Unassigned
                        </Text>
                      </View>
                    )}
                    <Pressable
                      onPress={() => {
                        setShowDetailModal(false);
                        setSelectedRequest(selectedRequestForDetail);
                        setShowOwnerModal(true);
                      }}
                    >
                      <Text style={styles.assignOwnerButton}>
                        {selectedRequestForDetail.assigned_owner ? 'Reassign' : 'Assign Owner'}
                      </Text>
                    </Pressable>
                  </View>
                </View>

                {/* Notes Section */}
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Notes</Text>
                  
                  {/* Add Note Form */}
                  <View style={styles.addNoteForm}>
                    <TextInput
                      style={styles.noteInput}
                      value={newNote}
                      onChangeText={setNewNote}
                      placeholder="Add a note..."
                      multiline
                      numberOfLines={2}
                      textAlignVertical="top"
                    />
                    <Pressable
                      style={[styles.addNoteButton, (!newNote.trim() || addingNote) && styles.buttonDisabled]}
                      onPress={handleAddNote}
                      disabled={!newNote.trim() || addingNote}
                    >
                      {addingNote ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <Text style={styles.addNoteButtonText}>Add</Text>
                      )}
                    </Pressable>
                  </View>
                  
                  {/* Notes List */}
                  <View style={styles.notesList}>
                    {notes.length === 0 ? (
                      <Text style={styles.noNotesText}>No notes yet</Text>
                    ) : (
                      notes.map((note) => (
                        <View key={note.id} style={styles.noteCard}>
                          <View style={styles.noteContent}>
                            <Text style={styles.noteText}>{note.content}</Text>
                            <Pressable
                              onPress={() => handleDeleteNote(note.id)}
                              disabled={deletingNoteId === note.id}
                            >
                              {deletingNoteId === note.id ? (
                                <ActivityIndicator size="small" color="#6b7280" />
                              ) : (
                                <Trash2 size={16} color="#9ca3af" />
                              )}
                            </Pressable>
                          </View>
                          <View style={styles.noteFooter}>
                            <Text style={styles.noteDate}>
                              {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                            </Text>
                            <Text style={styles.noteAuthor}>
                              {note.creator?.full_name || 'Unknown User'}
                            </Text>
                          </View>
                        </View>
                      ))
                    )}
                  </View>
                </View>

                {/* Quotes Section */}
                <View style={styles.detailSection}>
                  <View style={styles.sectionTitleRow}>
                    <DollarSign size={16} color="#6b7280" />
                    <Text style={styles.detailSectionTitle}>Quotes/Estimates</Text>
                  </View>
                  
                  {loadingQuotes ? (
                    <View style={styles.loadingContainer}>
                      <ActivityIndicator size="small" color="#9ca3af" />
                    </View>
                  ) : quotes.length === 0 ? (
                    <Text style={styles.noDataText}>No quotes yet</Text>
                  ) : (
                    <View style={styles.quotesList}>
                      {quotes.map((quote) => {
                        const quoteStatusColors = getQuoteStatusColor(quote.status);
                        return (
                          <View key={quote.id} style={styles.quoteCard}>
                            <View style={styles.quoteHeader}>
                              <View style={styles.quoteContent}>
                                <View style={styles.quoteTitleRow}>
                                  <Text style={styles.quoteProviderName}>
                                    {quote.service_provider?.company_name || 'Unknown Provider'}
                                  </Text>
                                  <View style={[styles.badge, { backgroundColor: quoteStatusColors.backgroundColor }]}>
                                    <Text style={[styles.badgeText, { color: quoteStatusColors.color }]}>
                                      {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                                    </Text>
                                  </View>
                                </View>
                                {quote.service_type && (
                                  <Text style={styles.quoteDetail}>
                                    <Text style={styles.quoteDetailLabel}>Service Type:</Text> {quote.service_type}
                                  </Text>
                                )}
                                {quote.work_description && (
                                  <Text style={styles.quoteDescription}>{quote.work_description}</Text>
                                )}
                                {quote.amount !== null && (
                                  <Text style={styles.quoteAmount}>{formatCurrency(quote.amount)}</Text>
                                )}
                                {quote.notes && (
                                  <Text style={styles.quoteNotes}>{quote.notes}</Text>
                                )}
                                <View style={styles.quoteMeta}>
                                  {quote.quote_date && (
                                    <Text style={styles.quoteMetaText}>
                                      Quote Date: {new Date(quote.quote_date).toLocaleDateString()}
                                    </Text>
                                  )}
                                  {quote.expires_at && (
                                    <Text style={styles.quoteMetaText}>
                                      Expires: {new Date(quote.expires_at).toLocaleDateString()}
                                    </Text>
                                  )}
                                  {(quote as any).attachment_count > 0 && (
                                    <View style={styles.metaItem}>
                                      <Paperclip size={12} color="#6b7280" />
                                      <Text style={styles.quoteMetaText}>
                                        {(quote as any).attachment_count} file{((quote as any).attachment_count !== 1) ? 's' : ''}
                                      </Text>
                                    </View>
                                  )}
                                </View>
                                {/* Attachments List */}
                                {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                                  <View style={styles.attachmentsContainer}>
                                    {quoteAttachments[quote.id].map((attachment: any) => (
                                      <Pressable
                                        key={attachment.id}
                                        style={styles.attachmentItem}
                                        onPress={() => downloadAttachment(attachment)}
                                      >
                                        <View style={styles.attachmentContent}>
                                          {getFileIcon(attachment.file_type)}
                                          <View style={styles.attachmentInfo}>
                                            <Text style={styles.attachmentName} numberOfLines={1}>
                                              {attachment.file_name}
                                            </Text>
                                            <Text style={styles.attachmentSize}>
                                              {formatFileSize(attachment.file_size)}
                                            </Text>
                                          </View>
                                        </View>
                                        <View style={styles.attachmentActions}>
                                          {isImageFile(attachment.file_type) && (
                                            <Pressable
                                              style={styles.attachmentAction}
                                              onPress={(e) => {
                                                e.stopPropagation();
                                                viewImage(attachment);
                                              }}
                                            >
                                              <Eye size={12} color="#6b7280" />
                                            </Pressable>
                                          )}
                                          <Pressable
                                            style={styles.attachmentAction}
                                            onPress={(e) => {
                                              e.stopPropagation();
                                              downloadAttachment(attachment);
                                            }}
                                          >
                                            <Download size={12} color="#6b7280" />
                                          </Pressable>
                                        </View>
                                      </Pressable>
                                    ))}
                                  </View>
                                )}
                              </View>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  )}
                </View>

                {/* Expenses Section */}
                <View style={styles.detailSection}>
                  <View style={styles.sectionTitleRow}>
                    <DollarSign size={16} color="#6b7280" />
                    <Text style={styles.detailSectionTitle}>Expenses</Text>
                  </View>
                  
                  {/* Expense Summary */}
                  {expenses.length > 0 && (
                    <View style={styles.expenseSummary}>
                      <View style={styles.expenseSummaryRow}>
                        <View style={styles.expenseSummaryItem}>
                          <Text style={styles.expenseSummaryLabel}>Total</Text>
                          <Text style={styles.expenseSummaryValue}>{formatCurrency(calculateTotalExpenses())}</Text>
                        </View>
                        <View style={styles.expenseSummaryItem}>
                          <Text style={styles.expenseSummaryLabel}>Paid</Text>
                          <Text style={[styles.expenseSummaryValue, { color: '#16a34a' }]}>{formatCurrency(calculatePaidExpenses())}</Text>
                        </View>
                        <View style={styles.expenseSummaryItem}>
                          <Text style={styles.expenseSummaryLabel}>Homeowner Outstanding</Text>
                          <Text style={[styles.expenseSummaryValue, { color: '#dc2626' }]}>{formatCurrency(calculateHomeownerOutstandingExpenses())}</Text>
                        </View>
                        <View style={styles.expenseSummaryItem}>
                          <Text style={styles.expenseSummaryLabel}>Contractor Outstanding</Text>
                          <Text style={[styles.expenseSummaryValue, { color: '#c2410c' }]}>{formatCurrency(calculateContractorOutstandingExpenses())}</Text>
                        </View>
                      </View>
                    </View>
                  )}

                  {loadingExpenses ? (
                    <View style={styles.loadingContainer}>
                      <ActivityIndicator size="small" color="#9ca3af" />
                    </View>
                  ) : expenses.length === 0 ? (
                    <Text style={styles.noDataText}>No expenses yet</Text>
                  ) : (
                    <View style={styles.expensesList}>
                      {expenses.map((expense) => (
                        <View key={expense.id} style={styles.expenseCard}>
                          <View style={styles.expenseContent}>
                            <View style={styles.expenseHeader}>
                              <Text style={styles.expenseAmount}>{formatCurrency(expense.amount)}</Text>
                              <View style={[styles.badge, { backgroundColor: expense.billable_to_homeowner ? (expense.is_paid ? '#dcfce7' : '#fee2e2') : '#f3f4f6' }]}>
                                <Text style={[styles.badgeText, { color: expense.billable_to_homeowner ? (expense.is_paid ? '#16a34a' : '#dc2626') : '#6b7280' }]}>
                                  {expense.billable_to_homeowner ? (expense.is_paid ? 'Homeowner Paid' : 'Homeowner Unpaid') : 'Not Billable'}
                                </Text>
                              </View>
                              <View style={[styles.badge, { backgroundColor: expense.needs_contractor_payment ? (expense.contractor_paid ? '#dcfce7' : '#ffedd5') : '#f3f4f6' }]}>
                                <Text style={[styles.badgeText, { color: expense.needs_contractor_payment ? (expense.contractor_paid ? '#16a34a' : '#c2410c') : '#6b7280' }]}>
                                  {expense.needs_contractor_payment ? (expense.contractor_paid ? 'Contractor Paid' : 'Contractor Unpaid') : 'No Contractor Payment'}
                                </Text>
                              </View>
                              {expense.quote && (
                                <View style={[styles.badge, { backgroundColor: '#dbeafe' }]}>
                                  <Text style={[styles.badgeText, { color: '#2563eb' }]}>Linked</Text>
                                </View>
                              )}
                            </View>
                            {expense.description && (
                              <Text style={styles.expenseDescription}>{expense.description}</Text>
                            )}
                            <View style={styles.expenseMeta}>
                              {expense.expense_date && (
                                <View style={styles.metaItem}>
                                  <Calendar size={12} color="#6b7280" />
                                  <Text style={styles.expenseMetaText}>
                                    {new Date(expense.expense_date).toLocaleDateString()}
                                  </Text>
                                </View>
                              )}
                              {expense.category && (
                                <Text style={styles.expenseMetaText}>
                                  <Text style={styles.expenseMetaLabel}>Category:</Text> {expense.category}
                                </Text>
                              )}
                              {expense.vendor && (
                                <Text style={styles.expenseMetaText}>
                                  <Text style={styles.expenseMetaLabel}>Vendor:</Text> {expense.vendor}
                                </Text>
                              )}
                              <Text style={styles.expenseMetaText}>
                                <Text style={styles.expenseMetaLabel}>Billable to Homeowner:</Text> {expense.billable_to_homeowner ? 'Yes' : 'No'}
                              </Text>
                              <Text style={styles.expenseMetaText}>
                                <Text style={styles.expenseMetaLabel}>Needs Contractor Payment:</Text> {expense.needs_contractor_payment ? 'Yes' : 'No'}
                              </Text>
                            </View>
                            <View style={styles.paymentInfo}>
                              <Text style={styles.paymentText}>
                                <Text style={styles.expenseMetaLabel}>Homeowner Payment:</Text>{' '}
                                {expense.billable_to_homeowner
                                  ? (expense.is_paid
                                    ? `${expense.payment_date ? new Date(expense.payment_date).toLocaleDateString() : 'N/A'} - ${getPaymentMethodLabel(expense.payment_method)}`
                                    : 'Pending')
                                  : 'Not required'}
                              </Text>
                              <Text style={styles.paymentText}>
                                <Text style={styles.expenseMetaLabel}>Contractor Payment:</Text>{' '}
                                {expense.needs_contractor_payment
                                  ? (expense.contractor_paid
                                    ? `${expense.contractor_payment_date ? new Date(expense.contractor_payment_date).toLocaleDateString() : 'N/A'} - ${getPaymentMethodLabel(expense.contractor_payment_method)}`
                                    : 'Pending')
                                  : 'Not required'}
                              </Text>
                            </View>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>

                <View style={styles.detailModalFooter}>
                  <Pressable
                    style={styles.viewDetailsButton}
                    onPress={(e) => {
                      const nativeEvent = e.nativeEvent as unknown as MouseEvent;
                      if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
                        window.open(`/admin/service-request/${selectedRequestForDetail.id}`, '_blank');
                      } else {
                        navigate(`/admin/service-request/${selectedRequestForDetail.id}`);
                      }
                    }}
                  >
                    <ExternalLink size={16} color="#ffffff" />
                    <Text style={styles.viewDetailsButtonText}>View Full Details</Text>
                  </Pressable>
                  <View style={styles.detailModalFooterActions}>
                    <Pressable
                      style={styles.deleteButton}
                      onPress={() => {
                        setShowDetailModal(false);
                        setShowDeleteConfirmModal(true);
                      }}
                    >
                      <Trash2 size={16} color="#ffffff" />
                      <Text style={styles.deleteButtonText}>Delete Request</Text>
                    </Pressable>
                    <Pressable
                      style={styles.closeButton}
                      onPress={() => setShowDetailModal(false)}
                    >
                      <Text style={styles.closeButtonText}>Close</Text>
                    </Pressable>
                  </View>
                </View>
              </WebScrollView>
            </View>
          </View>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        visible={showStatusModal && !!selectedRequest}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setShowStatusModal(false);
          setSelectedRequest(null);
        }}
      >
        {selectedRequest && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Update Request Status</Text>
                <Pressable
                  onPress={() => {
                    setShowStatusModal(false);
                    setSelectedRequest(null);
                  }}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.modalBody}>
                <View style={styles.modalInfo}>
                  <Text style={styles.modalInfoTitle}>{selectedRequest.title}</Text>
                  <Text style={styles.modalInfoText}>
                    Current Status: {selectedRequest.status.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </Text>
                </View>

                <View style={styles.statusGrid}>
                  {(['pending', 'in_progress', 'completed', 'cancelled'] as const).map((status) => {
                    const statusColors = getStatusColor(status);
                    const isCurrentStatus = status === selectedRequest.status;
                    const isDisabled = isCurrentStatus || updatingId === selectedRequest.id;
                    return (
                      <Pressable
                        key={status}
                        style={[
                          styles.statusButton,
                          isCurrentStatus && styles.statusButtonDisabled,
                          !isCurrentStatus && { backgroundColor: statusColors.backgroundColor }
                        ]}
                        onPress={() => handleStatusUpdate(selectedRequest.id, status)}
                        disabled={isDisabled}
                      >
                        <CheckCircle2 size={16} color={isCurrentStatus ? '#9ca3af' : statusColors.color} />
                        <Text style={[
                          styles.statusButtonText,
                          isCurrentStatus && styles.statusButtonTextDisabled,
                          !isCurrentStatus && { color: statusColors.color }
                        ]}>
                          {status.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
        )}
      </Modal>

      {/* Schedule Visit Modal */}
      <Modal
        visible={showScheduleVisitModal && !!selectedRequestForVisit}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setShowScheduleVisitModal(false);
          setSelectedRequestForVisit(null);
          setVisitFormData({
            scheduled_date: '',
            estimated_duration_minutes: visitDurationDefaults.service,
            assigned_to: '',
            service_provider_id: '',
            visit_notes: '',
            visit_type: 'service'
          });
        }}
      >
        {selectedRequestForVisit && (
          <View style={styles.modalOverlay}>
            <View style={styles.visitModalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Schedule Service Visit</Text>
                <Pressable
                  onPress={() => {
                    setShowScheduleVisitModal(false);
                    setSelectedRequestForVisit(null);
                    setVisitFormData({
                      scheduled_date: '',
                      estimated_duration_minutes: visitDurationDefaults.service,
                      assigned_to: '',
                      service_provider_id: '',
                      visit_notes: '',
                      visit_type: 'service'
                    });
                  }}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.visitRequestInfo}>
                <Text style={styles.visitRequestTitle}>{selectedRequestForVisit.title}</Text>
                <Text style={styles.visitRequestOwner}>
                  {selectedRequestForVisit.home?.owner?.full_name || 'Unknown Owner'}
                </Text>
                <Text style={styles.visitRequestDescription}>{selectedRequestForVisit.description}</Text>
                <Text style={styles.visitRequestAddress}>
                  {selectedRequestForVisit.home?.address}, {selectedRequestForVisit.home?.city}
                </Text>
              </View>

              <WebScrollView style={styles.visitModalBody} contentContainerStyle={styles.visitModalBodyContent}>
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Visit Type</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={visitFormData.visit_type}
                      onValueChange={(value) => {
                        const visitType = value as 'maintenance' | 'service' | 'walkthrough';
                        setVisitFormData({
                          ...visitFormData,
                          visit_type: visitType,
                          estimated_duration_minutes: visitDurationDefaults[visitType],
                        });
                      }}
                      style={styles.picker}
                    >
                      <Picker.Item label="Service Visit" value="service" />
                      <Picker.Item label="Maintenance Visit" value="maintenance" />
                      <Picker.Item label="Walkthrough" value="walkthrough" />
                    </Picker>
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Scheduled Date & Time (Optional)</Text>
                  {Platform.OS === 'web' ? (
                    <input
                      type="datetime-local"
                      value={visitFormData.scheduled_date}
                      onChange={(e) => setVisitFormData({ ...visitFormData, scheduled_date: (e as any).target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        outline: 'none',
                      }}
                    />
                  ) : (
                    <TextInput
                      style={styles.formInput}
                      value={visitFormData.scheduled_date}
                      onChangeText={(value) => setVisitFormData({ ...visitFormData, scheduled_date: value })}
                      placeholder="YYYY-MM-DDTHH:mm"
                    />
                  )}
                  <Text style={styles.formHint}>
                    Leave empty for visit requests pending homeowner confirmation
                  </Text>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Duration</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={visitFormData.estimated_duration_minutes}
                      onValueChange={(value) =>
                        setVisitFormData({
                          ...visitFormData,
                          estimated_duration_minutes: Number(value),
                        })
                      }
                      style={styles.picker}
                    >
                      {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                        <Picker.Item
                          key={minutes}
                          label={formatVisitDurationLabel(minutes)}
                          value={minutes}
                        />
                      ))}
                    </Picker>
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Assigned To</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={visitFormData.assigned_to}
                      onValueChange={(value) => setVisitFormData({ ...visitFormData, assigned_to: value })}
                      style={styles.picker}
                    >
                      <Picker.Item label="Unassigned" value="" />
                      {systemManagers.map((manager) => (
                        <Picker.Item key={manager.id} label={manager.full_name} value={manager.full_name} />
                      ))}
                    </Picker>
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Service Provider (Optional)</Text>
                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={visitFormData.service_provider_id}
                      onValueChange={(value) => setVisitFormData({ ...visitFormData, service_provider_id: value })}
                      style={styles.picker}
                    >
                      <Picker.Item label="None - Internal/Unassigned" value="" />
                      {serviceProviders.map((provider) => (
                        <Picker.Item key={provider.id} label={provider.company_name} value={provider.id} />
                      ))}
                    </Picker>
                  </View>
                  {visitFormData.service_provider_id && (
                    <Text style={styles.formHint}>
                      {serviceProviders.find(p => p.id === visitFormData.service_provider_id)?.phone || 'No phone'} | {' '}
                      {serviceProviders.find(p => p.id === visitFormData.service_provider_id)?.email || 'No email'}
                    </Text>
                  )}
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Visit Notes (Optional)</Text>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={visitFormData.visit_notes}
                    onChangeText={(value) => setVisitFormData({ ...visitFormData, visit_notes: value })}
                    placeholder="Special instructions or notes..."
                    multiline
                    numberOfLines={3}
                    textAlignVertical="top"
                  />
                </View>

                {error && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{error}</Text>
                  </View>
                )}

                <View style={styles.modalActions}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={() => {
                      setShowScheduleVisitModal(false);
                      setSelectedRequestForVisit(null);
                      setVisitFormData({
                        scheduled_date: '',
                        estimated_duration_minutes: visitDurationDefaults.service,
                        assigned_to: '',
                        service_provider_id: '',
                        visit_notes: '',
                        visit_type: 'service'
                      });
                    }}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.saveButton, schedulingVisit && styles.buttonDisabled]}
                    onPress={handleScheduleVisit}
                    disabled={schedulingVisit}
                  >
                    {schedulingVisit ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.saveButtonText}>Schedule Visit</Text>
                    )}
                  </Pressable>
                </View>
              </WebScrollView>
            </View>
          </View>
        )}
      </Modal>

      {/* Assign Owner Modal */}
      <Modal
        visible={showOwnerModal && !!selectedRequest}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setShowOwnerModal(false);
          setSelectedRequest(null);
        }}
      >
        {selectedRequest && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Assign Owner</Text>
                <Pressable
                  onPress={() => {
                    setShowOwnerModal(false);
                    setSelectedRequest(null);
                  }}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.modalBody}>
                <View style={styles.modalInfo}>
                  <Text style={styles.modalInfoTitle}>{selectedRequest.title}</Text>
                  <Text style={styles.modalInfoText}>Assign a system manager to take ownership of this request</Text>
                </View>

                <View style={styles.ownerList}>
                  {systemManagers.map((manager) => (
                    <Pressable
                      key={manager.id}
                      style={[styles.ownerItem, updatingId === selectedRequest.id && styles.buttonDisabled]}
                      onPress={() => {
                        handleOwnerAssignment(selectedRequest.id, manager.id);
                        setShowOwnerModal(false);
                        setSelectedRequest(null);
                      }}
                      disabled={updatingId === selectedRequest.id}
                    >
                      <UserCheck size={20} color="#9ca3af" />
                      <Text style={styles.ownerItemText}>{manager.full_name}</Text>
                    </Pressable>
                  ))}
                  
                  {/* Unassign option */}
                  <Pressable
                    style={[styles.ownerItem, updatingId === selectedRequest.id && styles.buttonDisabled]}
                    onPress={() => {
                      handleOwnerAssignment(selectedRequest.id, null);
                      setShowOwnerModal(false);
                      setSelectedRequest(null);
                    }}
                    disabled={updatingId === selectedRequest.id}
                  >
                    <User size={20} color="#9ca3af" />
                    <Text style={styles.ownerItemTextUnassigned}>Unassigned</Text>
                  </Pressable>
                </View>

                {updatingId === selectedRequest.id && (
                  <View style={styles.updatingContainer}>
                    <ActivityIndicator size="small" color="#9ca3af" />
                    <Text style={styles.updatingText}>Updating...</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        )}
      </Modal>

      {/* Priority Update Modal */}
      <Modal
        visible={showPriorityModal && !!selectedRequest}
        transparent
        animationType="slide"
        onRequestClose={() => {
          setShowPriorityModal(false);
          setSelectedRequest(null);
        }}
      >
        {selectedRequest && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Update Request Priority</Text>
                <Pressable
                  onPress={() => {
                    setShowPriorityModal(false);
                    setSelectedRequest(null);
                  }}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.modalBody}>
                <View style={styles.modalInfo}>
                  <Text style={styles.modalInfoTitle}>{selectedRequest.title}</Text>
                  <Text style={styles.modalInfoText}>
                    Current Priority: {selectedRequest.priority.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </Text>
                </View>

                <View style={styles.priorityGrid}>
                  {(['low', 'normal', 'high', 'urgent'] as const).map((priority) => {
                    const priorityColors = getPriorityColor(priority);
                    const isCurrentPriority = priority === selectedRequest.priority;
                    const isDisabled = isCurrentPriority || updatingId === selectedRequest.id;
                    return (
                      <Pressable
                        key={priority}
                        style={[
                          styles.priorityButton,
                          isCurrentPriority && styles.priorityButtonDisabled,
                          !isCurrentPriority && { backgroundColor: priorityColors.backgroundColor }
                        ]}
                        onPress={() => handlePriorityUpdate(selectedRequest.id, priority)}
                        disabled={isDisabled}
                      >
                        <CheckCircle2 size={16} color={isCurrentPriority ? '#9ca3af' : priorityColors.color} />
                        <Text style={[
                          styles.priorityButtonText,
                          isCurrentPriority && styles.priorityButtonTextDisabled,
                          !isCurrentPriority && { color: priorityColors.color }
                        ]}>
                          {priority.charAt(0).toUpperCase() + priority.slice(1)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirmModal && !!selectedRequestForDetail}
        transparent
        animationType="slide"
        onRequestClose={() => setShowDeleteConfirmModal(false)}
      >
        {selectedRequestForDetail && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { color: '#dc2626' }]}>Delete Service Request</Text>
                <Pressable
                  onPress={() => setShowDeleteConfirmModal(false)}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.modalBody}>
                <View style={styles.deleteConfirmContent}>
                  <Trash2 size={24} color="#ef4444" />
                  <View style={styles.deleteConfirmText}>
                    <Text style={styles.deleteConfirmMessage}>
                      Are you sure you want to delete this service request? This action cannot be undone.
                    </Text>
                    <View style={styles.deleteConfirmInfo}>
                      <Text style={styles.deleteConfirmTitle}>{selectedRequestForDetail.title}</Text>
                      <Text style={styles.deleteConfirmOwner}>
                        {selectedRequestForDetail.home?.owner?.full_name || 'Unknown Owner'}
                      </Text>
                    </View>
                  </View>
                </View>

                <View style={styles.modalActions}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={() => setShowDeleteConfirmModal(false)}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.deleteConfirmButton, deletingRequest && styles.buttonDisabled]}
                    onPress={() => handleDeleteRequest(selectedRequestForDetail.id)}
                    disabled={deletingRequest}
                  >
                    {deletingRequest ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <>
                        <Trash2 size={16} color="#ffffff" />
                        <Text style={styles.deleteConfirmButtonText}>Delete Request</Text>
                      </>
                    )}
                  </Pressable>
                </View>
              </View>
            </View>
          </View>
        )}
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <Pressable style={styles.imagePreviewOverlay} onPress={() => setPreviewImage(null)}>
          <View style={styles.imagePreviewContainer}>
            <Pressable
              style={styles.imagePreviewClose}
              onPress={() => setPreviewImage(null)}
            >
              <X size={24} color="#ffffff" />
            </Pressable>
            {previewImage && (
              <Image
                source={{ uri: previewImage }}
                style={styles.previewImage}
                resizeMode="contain"
              />
            )}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#dc2626',
    marginBottom: 4,
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  tabScroll: {
    flex: 1,
    minHeight: 0,
  },
  tabScrollContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    paddingBottom: 32,
    ...shadows.sm,
  },
  header: {
    flexDirection: 'column',
    marginBottom: 24,
    gap: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    minWidth: Platform.OS === 'web' ? 250 : 200,
    flex: Platform.OS === 'web' ? 0 : 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#000000',
    paddingVertical: 0,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  filterContainer: {
    position: 'relative',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#000000',
  },
  filterButtonIcon: {
    fontSize: 12,
    color: '#6b7280',
  },
  queueFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  queueFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  queueFilterChipActive: {
    backgroundColor: '#111827',
    borderColor: '#111827',
  },
  queueFilterChipText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  queueFilterChipTextActive: {
    color: '#ffffff',
  },
  queueFilterChipCount: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
  },
  queueFilterChipCountActive: {
    color: '#e5e7eb',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  statusFilterDropdown: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...shadows.md,
    minWidth: 192,
  },
  ownerFilterDropdown: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...shadows.md,
    minWidth: 200,
    maxHeight: 400,
  },
  priorityFilterDropdown: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...shadows.md,
    minWidth: 192,
  },
  statusFilterItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  statusFilterDivider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 4,
  },
  statusFilterCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#d1d5db',
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  statusFilterText: {
    fontSize: 14,
    color: '#000000',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  clearSearchButton: {
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
  },
  clearSearchButtonText: {
    fontSize: 14,
    color: '#4b5563',
    fontWeight: '500',
  },
  summaryWidget: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 20,
    marginBottom: 24,
    gap: 20,
  },
  summaryWidgetTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  summarySection: {
    gap: 12,
  },
  summarySectionTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  statusSummaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statusSummaryItem: {
    flex: 1,
    minWidth: 100,
    alignItems: 'center',
    gap: 8,
  },
  statusSummaryBadge: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    ...shadows.sm,
  },
  statusSummaryCount: {
    fontSize: 20,
    fontWeight: '700',
  },
  statusSummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
    textAlign: 'center',
  },
  capacitySummaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  capacitySummaryItem: {
    flex: 1,
    minWidth: 140,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    gap: 8,
  },
  capacitySummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  capacitySummaryValue: {
    fontSize: 20,
    color: '#111827',
    fontWeight: '700',
  },
  capacitySummaryValueAlert: {
    color: '#b91c1c',
  },
  financialSummaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  financialSummaryItem: {
    flex: 1,
    minWidth: 140,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    gap: 8,
  },
  financialSummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  financialSummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  financialSummaryAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  viewToggleRow: {
    flexDirection: 'row',
    gap: 0,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#ffffff',
  },
  viewToggleButtonActive: {
    backgroundColor: '#111827',
  },
  viewToggleButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
  },
  viewToggleButtonTextActive: {
    color: '#ffffff',
  },
  requestsList: {
    gap: 16,
  },
  requestsListCompact: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  compactCard: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compactCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  compactCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  compactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  compactPriorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
    minWidth: 0,
  },
  compactOwnerName: {
    fontSize: 12,
    color: '#6b7280',
    flexShrink: 0,
    maxWidth: 140,
  },
  compactCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  compactAssignedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    maxWidth: 120,
  },
  compactAssignedText: {
    fontSize: 11,
    color: '#16a34a',
    fontWeight: '500',
  },
  compactExpenseFlag: {
    fontSize: 11,
    color: '#dc2626',
    fontWeight: '600',
  },
  compactDetailsButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#111827',
    justifyContent: 'center',
    alignItems: 'center',
  },
  compactCardBody: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#fafafa',
    gap: 10,
  },
  compactCardActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  requestCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  requestContent: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  requestInfo: {
    minWidth: 0,
    width: '100%',
  },
  capacityPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#ecfeff',
    borderWidth: 1,
    borderColor: '#a5f3fc',
  },
  capacityPillText: {
    fontSize: 12,
    color: '#0f766e',
    fontWeight: '600',
  },
  capacityPillOverLimit: {
    fontSize: 12,
    color: '#b91c1c',
    fontWeight: '700',
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  requestOwner: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
    marginBottom: 4,
  },
  requestDescription: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 12,
  },
  requestMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  metaTextAssigned: {
    fontSize: 12,
    color: '#16a34a',
    fontWeight: '500',
  },
  acceptedQuotesText: {
    fontSize: 12,
    color: '#16a34a',
    fontWeight: '500',
  },
  expensesSummaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    marginTop: 12,
  },
  expensesSummaryTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  expensesSummaryRow: {
    flexDirection: 'row',
    gap: 16,
  },
  expensesSummaryItem: {
    flex: 1,
  },
  expensesSummaryLabel: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 2,
  },
  expensesSummaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  requestActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  visitScheduledBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#dcfce7',
  },
  visitScheduledText: {
    fontSize: 12,
    color: '#16a34a',
  },
  scheduleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#dbeafe',
  },
  scheduleButtonText: {
    fontSize: 12,
    color: '#2563eb',
  },
  assignButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
  },
  assignButtonText: {
    fontSize: 12,
    color: '#4b5563',
  },
  backburnerButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  backburnerButtonActive: {
    borderColor: '#7c3aed',
    backgroundColor: '#f5f3ff',
  },
  backburnerButtonText: {
    fontSize: 12,
    color: '#4b5563',
    fontWeight: '500',
  },
  backburnerButtonTextActive: {
    color: '#6d28d9',
  },
  lowPriorityBackburnerBadge: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#fef3c7',
    borderWidth: 1,
    borderColor: '#fcd34d',
  },
  lowPriorityBackburnerBadgeText: {
    fontSize: 12,
    color: '#92400e',
    fontWeight: '500',
  },
  statusButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  statusButtonDisabled: {
    backgroundColor: '#f3f4f6',
  },
  statusButtonTextDisabled: {
    color: '#9ca3af',
  },
  viewDetailsButtonSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#000000',
  },
  viewDetailsButtonSmallText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '500',
  },
  detailModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...shadows.md,
    maxWidth: 600,
    width: '100%',
    maxHeight: '90%',
  },
  detailModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  detailModalHeaderLeft: {
    flex: 1,
  },
  detailModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  detailModalOwner: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
    marginBottom: 12,
  },
  detailModalMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  detailModalBody: {
    maxHeight: 500,
  },
  detailModalBodyContent: {
    padding: 24,
    gap: 24,
  },
  detailSection: {
    gap: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  detailSectionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  descriptionBox: {
    backgroundColor: '#f9fafb',
    padding: 16,
    borderRadius: 8,
  },
  descriptionText: {
    fontSize: 14,
    color: '#374151',
  },
  detailRow: {
    flexDirection: 'row',
    gap: 24,
  },
  detailColumn: {
    flex: 1,
  },
  detailValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ownerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ownerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ownerName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#16a34a',
  },
  ownerUnassigned: {
    fontSize: 14,
    color: '#6b7280',
  },
  assignOwnerButton: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
  },
  addNoteForm: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  noteInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    minHeight: 60,
    textAlignVertical: 'top',
  },
  addNoteButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addNoteButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  notesList: {
    gap: 12,
  },
  noteCard: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
  },
  noteContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  noteFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  noteAuthor: {
    fontSize: 12,
    color: '#6b7280',
  },
  noNotesText: {
    fontSize: 14,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  quotesList: {
    gap: 12,
  },
  quoteCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
  },
  quoteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  quoteContent: {
    flex: 1,
  },
  quoteTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  quoteProviderName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  quoteDetail: {
    fontSize: 12,
    color: '#4b5563',
    marginBottom: 4,
  },
  quoteDetailLabel: {
    fontWeight: '500',
  },
  quoteDescription: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 4,
  },
  quoteAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  quoteNotes: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 4,
  },
  quoteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  quoteMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentsContainer: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  attachmentItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 6,
    padding: 6,
    marginBottom: 4,
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 6,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#111827',
  },
  attachmentSize: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentActions: {
    flexDirection: 'row',
    gap: 4,
  },
  attachmentAction: {
    padding: 4,
  },
  expensesList: {
    gap: 8,
  },
  expenseCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
  },
  expenseContent: {
    flex: 1,
  },
  expenseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  expenseAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  expenseDescription: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 4,
  },
  expenseMeta: {
    gap: 4,
    marginTop: 4,
  },
  expenseMetaText: {
    fontSize: 12,
    color: '#4b5563',
  },
  expenseMetaLabel: {
    fontWeight: '500',
  },
  paymentInfo: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  paymentText: {
    fontSize: 12,
    color: '#4b5563',
    marginBottom: 2,
  },
  expenseSummary: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    marginBottom: 12,
  },
  expenseSummaryRow: {
    flexDirection: 'row',
    gap: 8,
  },
  expenseSummaryItem: {
    flex: 1,
    alignItems: 'center',
  },
  expenseSummaryLabel: {
    fontSize: 12,
    color: '#4b5563',
    marginBottom: 4,
  },
  expenseSummaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  noDataText: {
    fontSize: 14,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  detailModalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#2563eb',
    borderRadius: 6,
  },
  viewDetailsButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  detailModalFooterActions: {
    flexDirection: 'row',
    gap: 8,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#dc2626',
    borderRadius: 6,
  },
  deleteButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  closeButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 6,
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...shadows.md,
    maxWidth: 400,
    width: '100%',
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
  },
  modalBody: {
    gap: 16,
  },
  modalInfo: {
    gap: 4,
  },
  modalInfoTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  modalInfoText: {
    fontSize: 14,
    color: '#4b5563',
  },
  statusGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  priorityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  priorityButton: {
    flex: 1,
    minWidth: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
  priorityButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  priorityButtonDisabled: {
    backgroundColor: '#f3f4f6',
  },
  priorityButtonTextDisabled: {
    color: '#9ca3af',
  },
  visitModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...shadows.md,
    maxWidth: 400,
    width: '100%',
    maxHeight: '90%',
  },
  visitRequestInfo: {
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  visitRequestTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  visitRequestOwner: {
    fontSize: 12,
    fontWeight: '500',
    color: '#2563eb',
    marginBottom: 4,
  },
  visitRequestDescription: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 4,
  },
  visitRequestAddress: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
  },
  visitModalBody: {
    maxHeight: 400,
  },
  visitModalBodyContent: {
    paddingBottom: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  formHint: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 6,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  ownerList: {
    gap: 12,
  },
  ownerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  ownerItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  ownerItemTextUnassigned: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
  },
  updatingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    gap: 8,
  },
  updatingText: {
    fontSize: 14,
    color: '#4b5563',
  },
  deleteConfirmContent: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  deleteConfirmText: {
    flex: 1,
  },
  deleteConfirmMessage: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 12,
  },
  deleteConfirmInfo: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 6,
  },
  deleteConfirmTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 4,
  },
  deleteConfirmOwner: {
    fontSize: 12,
    color: '#6b7280',
  },
  deleteConfirmButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    backgroundColor: '#dc2626',
    borderRadius: 6,
  },
  deleteConfirmButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  imagePreviewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  imagePreviewContainer: {
    position: 'relative',
    maxWidth: '100%',
    maxHeight: '90%',
  },
  imagePreviewClose: {
    position: 'absolute',
    top: -40,
    right: 0,
    zIndex: 1,
  },
  previewImage: {
    width: '100%',
    maxWidth: 800,
    height: '100%',
    maxHeight: 600,
    borderRadius: 8,
  },
});

export default ServiceRequestsTab;